import { moduleEntries, modulePathForPage } from './admin-navigation';
import { AdminModuleIcon } from './admin-module-icon';
import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { readSafeCmsComponents } from '@travel/contracts';
import type { CmsBindingContext } from '@travel/contracts';

import {
  type AdminApiClient,
  ApiError,
  isRecord,
  textOf,
  unwrapList,
  unwrapRecord,
} from './api-client';
import type { AdminCaptchaChallenge, AdminLoginSession, JsonRecord } from './api-client';
import { ActionButton, Notice } from './admin-feedback';
import {
  CanonicalCityListPage,
  CanonicalDashboardPage,
  CanonicalProductListPage,
} from './canonical-admin';
import {
  CanonicalAppPage,
  CanonicalCityDetailPage,
  CanonicalLiveTransactionsPage,
  CanonicalProductPage,
  CanonicalSkuPage,
  CanonicalTagsSortPage,
  CanonicalTodoCenterPage,
} from './canonical-round02';
import { CanonicalCmsPage, CanonicalOrderPage } from './canonical-round03';
import { CanonicalMemberPage, CanonicalPaymentPage } from './canonical-round04';
import { CanonicalMarketingChannelPage } from './canonical-round05';
import { CanonicalMessageFinancePage, CanonicalSecurityReleasePage } from './canonical-round06';
import { BusinessSecurityMyAccessPage } from './business-security-workspace';
import { AccountCenterPage } from './account-center';
import { AdLandingHost } from './ad-landing-host';
import {
  AUTHORIZED_SCOPE,
  AuthorizedScopeApiClient,
  PAGE_DATASETS,
  type PageScope,
} from './authorized-scope-client';
import { PageScopeFilter, PageScopePagination } from './page-scope-filter';
import { adminPages, matchPage, pathFor } from './page-registry';
import {
  CityCreateForm,
  CmsComponentForm,
  CmsPageCreateForm,
  RuntimeConfigForm,
  SkuCreateForm,
} from './typed-forms';
import { deriveViewKind } from './view-state';

type Params = Record<string, string>;
type LoadState<T> = { data?: T; error?: unknown; loading: boolean; reload: () => void };
interface AdminRuntime {
  api: AdminApiClient;
  appId: string;
  token: string;
  targetAppId: string;
  production: boolean;
  user: AdminLoginSession['user'] | null;
  expiresAt: string;
  configError?: string;
  setCredentials: (
    appId: string,
    token: string,
    session?: Pick<AdminLoginSession, 'user' | 'expiresAt'>,
  ) => void;
  logout: () => Promise<void>;
  setTargetAppId: (appId: string) => void;
  clearSession: () => void;
  updateDisplayName: (value: string) => void;
  pageScope: PageScope;
  setPageScope: (value: PageScope) => void;
}
const RuntimeContext = createContext<AdminRuntime | null>(null);
function useRuntime() {
  const value = useContext(RuntimeContext);
  if (!value) throw new Error('Admin runtime is unavailable');
  return value;
}

function useAsync<T>(key: string, loader: () => Promise<T>, dependency?: unknown): LoadState<T> {
  const [revision, setRevision] = useState(0);
  const [state, setState] = useState<Omit<LoadState<T>, 'reload'>>({ loading: true });
  useEffect(() => {
    let active = true;
    setState({ loading: true });
    void loader().then(
      (data) => active && setState({ loading: false, data }),
      (error: unknown) => active && setState({ loading: false, error }),
    );
    return () => {
      active = false;
    };
    // Request identity is fully represented by key.
  }, [key, revision, dependency]);
  return { ...state, reload: () => setRevision((value) => value + 1) };
}
function useGet(path?: string) {
  const { api } = useRuntime();
  return useAsync(
    path ?? 'disabled',
    () => (path ? api.get<unknown>(path) : Promise.resolve({ items: [] })),
    api,
  );
}
function usePublicGet(path: string) {
  return useAsync(path, async () => {
    const response = await fetch(`${import.meta.env.VITE_API_BASE_URL ?? ''}${path}`, {
      headers: { Accept: 'application/json' },
    });
    const payload: unknown = await response.json();
    if (!response.ok) {
      throw new ApiError(response.status, `HTTP_${response.status}`, '健康检查失败');
    }
    return payload;
  });
}
function navigate(event: React.MouseEvent<HTMLAnchorElement>, href: string) {
  if (event.button || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  event.preventDefault();
  history.pushState({}, '', href);
  window.dispatchEvent(new PopStateEvent('popstate'));
}
function Link({
  href,
  children,
  className,
  ...props
}: {
  href: string;
  children: React.ReactNode;
  className?: string;
} & Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href' | 'onClick'>) {
  return (
    <a {...props} href={href} className={className} onClick={(event) => navigate(event, href)}>
      {children}
    </a>
  );
}

export function AppSelector() {
  const runtime = useRuntime();
  const configurationAppId = window.location.pathname.match(
    /^\/admin\/apps\/([^/]+)\/(?:settings|cities|products|customer-service|payment-merchants)$/,
  )?.[1];
  const historyMode = /^\/admin\/(orders|finance|reports|commissions|channels)/.test(
    window.location.pathname,
  );
  const state = useGet(
    historyMode ? '/api/admin/m2/apps?includeDeleted=true' : '/api/admin/m2/apps',
  );
  const personalScope = useGet('/api/admin/m8/security/me');
  const scopeFallback = state.error instanceof ApiError && state.error.status === 403;
  const selectorState = scopeFallback ? personalScope : state;
  const apps = scopeFallback
    ? unwrapList(unwrapRecord(personalScope.data).apps)
    : unwrapList(state.data);
  useEffect(() => {
    const refresh = () => state.reload();
    window.addEventListener('travel:apps-changed', refresh);
    return () => window.removeEventListener('travel:apps-changed', refresh);
  }, [state.reload]);
  useEffect(() => {
    const first = apps[0];
    if (configurationAppId && apps.some((app) => textOf(app, 'id') === configurationAppId)) {
      if (runtime.targetAppId !== configurationAppId) runtime.setTargetAppId(configurationAppId);
      return;
    }
    if (
      !selectorState.loading &&
      !selectorState.error &&
      runtime.targetAppId &&
      !apps.some((app) => textOf(app, 'id') === runtime.targetAppId)
    )
      runtime.setTargetAppId(first ? textOf(first, 'id') : '');
    else if (!runtime.targetAppId && first) runtime.setTargetAppId(textOf(first, 'id'));
  }, [apps, runtime, configurationAppId]);
  return (
    <AppSelectorView
      state={selectorState}
      apps={apps}
      value={runtime.targetAppId}
      allowAll={!configurationAppId}
      onChange={(value) => {
        if (configurationAppId && value && value !== configurationAppId) {
          const nextPath = `/admin/apps/${encodeURIComponent(value)}/settings`;
          history.pushState({}, '', nextPath);
          window.dispatchEvent(new PopStateEvent('popstate'));
          if (window.location.pathname !== nextPath) return;
        }
        runtime.setTargetAppId(value);
      }}
    />
  );
}
export function AppSelectorView({
  state,
  apps,
  value,
  onChange,
  allowAll = true,
}: {
  state: LoadState<unknown>;
  apps: JsonRecord[];
  value: string;
  onChange: (value: string) => void;
  allowAll?: boolean;
}) {
  const kind = deriveViewKind(state.loading, state.error, apps.length);
  const statusText =
    kind === 'loading'
      ? '加载中…'
      : kind === 'permission'
        ? '无权读取小程序范围'
        : kind === 'error'
          ? '小程序范围暂不可用'
          : kind === 'empty'
            ? '暂无授权小程序'
            : undefined;
  return (
    <label className="app-selector" aria-label="选择小程序">
      <select value={value} disabled={kind !== 'ready'} onChange={(e) => onChange(e.target.value)}>
        {(allowAll || statusText) && <option value="">{statusText ?? '全部小程序'}</option>}
        {apps.map((app) => (
          <option key={textOf(app, 'id')} value={textOf(app, 'id')}>
            {textOf(app, 'displayName', 'name', 'appKey')}
            {app.deletedAt ? '（已删除 · 历史订单）' : ''}
          </option>
        ))}
      </select>
      {statusText && <small className={`app-selector-state ${kind}`}>{statusText}</small>}
    </label>
  );
}

export function CityTopSelector() {
  const { targetAppId } = useRuntime();
  const state = useGet(
    targetAppId ? `/api/admin/m2/apps/${encodeURIComponent(targetAppId)}` : undefined,
  );
  const personal = useGet('/api/admin/m8/security/me');
  const profile = unwrapRecord(personal.data);
  const fallback = state.error instanceof ApiError && state.error.status === 403;
  const scopedApp = unwrapList(profile.apps).find((app) => textOf(app, 'id') === targetAppId);
  const cities = fallback
    ? unwrapList(scopedApp?.cities)
    : isRecord(state.data) && Array.isArray(state.data.cities)
      ? state.data.cities
          .filter(isRecord)
          .map((item) => (isRecord(item.city) ? item.city : item))
          .filter(isRecord)
      : [];
  const selectorState = fallback ? personal : state;
  if (!Array.isArray(profile.visibleMenuIds) || !profile.visibleMenuIds.includes('ADM-B02'))
    return null;
  return (
    <label className="canonical-top-select" aria-label="进入城市管理范围">
      <select
        value=""
        disabled={
          !targetAppId ||
          selectorState.loading ||
          Boolean(selectorState.error) ||
          cities.length === 0
        }
        onChange={(event) => {
          if (!event.target.value) return;
          history.pushState({}, '', pathFor('ADM-B02', { id: event.target.value }));
          window.dispatchEvent(new PopStateEvent('popstate'));
        }}
      >
        <option value="">
          {selectorState.loading ? '城市加载中…' : cities.length ? '选择城市' : '暂无可用城市'}
        </option>
        {cities.map((city) => (
          <option key={textOf(city, 'id')} value={textOf(city, 'id')}>
            {textOf(city, 'name')}
          </option>
        ))}
      </select>
    </label>
  );
}

function AdminShell({
  children,
  pageId,
  title,
}: {
  children: React.ReactNode;
  pageId: string;
  title: string;
}) {
  const runtime = useRuntime();
  const [menuSearch, setMenuSearch] = useState('');
  const menuAccess = useGet('/api/admin/m8/security/me');
  const menuIds = unwrapRecord(menuAccess.data).visibleMenuIds;
  const visibleMenus = new Set(
    Array.isArray(menuIds)
      ? menuIds.filter((id): id is string => typeof id === 'string')
      : ['ADM-A07', 'ADM-A09'],
  );
  const accessibleEntries = (href: string) =>
    moduleEntries(href).filter((entry) => visibleMenus.has(entry.id));
  const activeModule = modulePathForPage(pageId);
  const [loggingOut, setLoggingOut] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [commandFeedback, setCommandFeedback] = useState<{
    message: string;
    tone: 'success' | 'error';
  }>();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => readSession('travel.admin.sidebarCollapsed') === 'true',
  );
  const [compactViewport, setCompactViewport] = useState(() => window.innerWidth <= 1100);
  const [compactMenuOpen, setCompactMenuOpen] = useState(false);
  useEffect(() => {
    const update = () => {
      const compact = window.innerWidth <= 1100;
      setCompactViewport(compact);
      if (!compact) setCompactMenuOpen(false);
    };
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);
  useEffect(() => {
    let dismissTimer: number | undefined;
    const receiveFeedback = (event: Event) => {
      const detail = (event as CustomEvent<unknown>).detail;
      if (
        !isRecord(detail) ||
        typeof detail.message !== 'string' ||
        (detail.tone !== 'success' && detail.tone !== 'error')
      )
        return;
      setCommandFeedback({ message: detail.message, tone: detail.tone });
      if (dismissTimer !== undefined) window.clearTimeout(dismissTimer);
      dismissTimer = window.setTimeout(() => setCommandFeedback(undefined), 3600);
    };
    window.addEventListener('travel:admin-command-feedback', receiveFeedback);
    return () => {
      window.removeEventListener('travel:admin-command-feedback', receiveFeedback);
      if (dismissTimer !== undefined) window.clearTimeout(dismissTimer);
    };
  }, []);
  useEffect(() => {
    if (!compactMenuOpen) return;
    const close = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setCompactMenuOpen(false);
    };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [compactMenuOpen]);
  const pathname = location.pathname;
  const scopedMenuPath = (path: string) =>
    path.startsWith('/admin/ad-landings') && runtime.pageScope.appId
      ? `${path}?appId=${encodeURIComponent(runtime.pageScope.appId)}`
      : path;
  const items = [
    ['⌂', '经营总览', '/admin/dashboard'],
    ['▦', '多小程序管理', '/admin/apps'],
    ['⌖', '城市与地区管理', '/admin/cities'],
    ['▤', '线路产品管理', '/admin/products'],
    ['▧', '广告页管理', '/admin/ad-landings'],
    ['▣', '可视化装修（CMS）', '/admin/cms/templates'],
    ['▥', '订单履约', '/admin/orders'],
    ['♙', '会员与用户', '/admin/members'],
    ['◇', '营销与分销', '/admin/marketing/coupons'],
    ['▱', '支付中心', '/admin/payments/merchants'],
    ['▧', '消息与通知', '/admin/messages/templates'],
    ['▥', '财务报表', '/admin/finance/order-profit'],
    ['⚙', '系统设置', '/admin/system/settings'],
    ['♜', '角色与权限', '/admin/security/users'],
    ['▦', '操作日志', '/admin/audit/overview'],
  ] as const;
  const productSubmenu = [['线路产品', '/admin/products']] as const;
  const activeProductSubmenu = (href: string) => href === '/admin/products';
  const expiryCopy = runtime.expiresAt
    ? `会话至 ${new Date(runtime.expiresAt).toLocaleString('zh-CN', {
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      })}`
    : '会话到期时间未提供';
  const sectionTitle = ['ADM-B01', 'ADM-B02', 'ADM-B03'].includes(pageId)
    ? '城市与地区管理'
    : pageId.startsWith('ADM-B')
      ? '多小程序管理'
      : pageId.startsWith('ADM-C')
        ? '线路产品'
        : pageId === 'ADM-A02'
          ? '经营总览'
          : title;
  return (
    <div
      className={`canonical-admin-shell erp-admin-shell${sidebarCollapsed ? ' is-sidebar-collapsed' : ''}${compactMenuOpen ? ' is-compact-navigation-open' : ''}`}
    >
      <aside className="canonical-sidebar">
        <div className="canonical-brand">
          <span className="canonical-brand__mark">旅</span>
          <div>
            <strong>
              城市一日游
              <br />
              综合旅游平台
            </strong>
            <small>中央运营管理后台</small>
          </div>
        </div>
        <label className="erp-menu-search">
          <span className="sr-only">查找功能</span>
          <input
            type="search"
            aria-label="查找功能"
            placeholder="查找功能…"
            value={menuSearch}
            onChange={(event) => setMenuSearch(event.target.value)}
          />
        </label>
        <nav
          aria-label="业务模块"
          className="canonical-menu"
          onClick={() => compactViewport && setCompactMenuOpen(false)}
        >
          {items
            .filter(([, , href]) => accessibleEntries(href).length > 0)
            .filter(
              ([, label, href]) =>
                !menuSearch.trim() ||
                label.includes(menuSearch.trim()) ||
                moduleEntries(href).some((page) => page.title.includes(menuSearch.trim())),
            )
            .map(([, label, href]) => {
              const active = activeModule === href;
              return (
                <React.Fragment key={href}>
                  <Link
                    href={scopedMenuPath(
                      accessibleEntries(href).find((entry) => entry.path === href)?.path ??
                        accessibleEntries(href)[0]?.path ??
                        href,
                    )}
                    className={active ? 'active' : ''}
                    title={label}
                  >
                    <span className="canonical-menu__icon">
                      <AdminModuleIcon href={href} />
                    </span>
                    <span>{label}</span>
                    <b>›</b>
                  </Link>
                  {(active || menuSearch.trim()) && (
                    <div className="canonical-submenu">
                      {(href === '/admin/products'
                        ? productSubmenu.map(([subLabel, subHref]) => ({
                            title: subLabel,
                            path: subHref,
                          }))
                        : accessibleEntries(href)
                      )
                        .filter(
                          (entry) =>
                            (href !== '/admin/products' || visibleMenus.has('ADM-C01')) &&
                            (!menuSearch.trim() ||
                              label.includes(menuSearch.trim()) ||
                              entry.title.includes(menuSearch.trim())),
                        )
                        .map((entry) => (
                          <Link
                            key={entry.path}
                            href={scopedMenuPath(entry.path)}
                            className={
                              (
                                href === '/admin/products'
                                  ? activeProductSubmenu(entry.path)
                                  : pathname === entry.path
                              )
                                ? 'active'
                                : ''
                            }
                          >
                            {entry.title}
                          </Link>
                        ))}
                    </div>
                  )}
                </React.Fragment>
              );
            })}
        </nav>
        <div className="canonical-sidebar__foot">
          <button
            type="button"
            aria-expanded={compactViewport ? compactMenuOpen : !sidebarCollapsed}
            aria-label={
              compactViewport
                ? compactMenuOpen
                  ? '关闭侧栏菜单'
                  : '展开侧栏菜单'
                : sidebarCollapsed
                  ? '展开侧栏菜单'
                  : '收起侧栏菜单'
            }
            title={
              compactViewport ? '打开完整导航' : sidebarCollapsed ? '展开侧栏菜单' : '收起侧栏菜单'
            }
            onClick={() => {
              if (compactViewport) {
                setCompactMenuOpen((current) => !current);
                return;
              }
              setSidebarCollapsed((current) => {
                const next = !current;
                sessionStorage.setItem('travel.admin.sidebarCollapsed', String(next));
                return next;
              });
            }}
          >
            <span aria-hidden="true">
              {compactViewport ? (compactMenuOpen ? '‹‹' : '››') : sidebarCollapsed ? '››' : '‹‹'}
            </span>
            <span className="canonical-sidebar__collapse-label">
              {compactViewport
                ? compactMenuOpen
                  ? '关闭菜单'
                  : '展开菜单'
                : sidebarCollapsed
                  ? '展开菜单'
                  : '收起菜单'}
            </span>
          </button>
          <small>运营管理平台</small>
        </div>
      </aside>
      {compactMenuOpen ? (
        <button
          type="button"
          className="canonical-sidebar-backdrop"
          aria-label="关闭侧栏菜单"
          onClick={() => setCompactMenuOpen(false)}
        />
      ) : null}
      <main className="canonical-workspace">
        <header className="canonical-topbar">
          <div className="canonical-topbar__title">
            <button
              type="button"
              className="erp-menu-toggle"
              aria-label="切换导航菜单"
              aria-expanded={compactViewport ? compactMenuOpen : !sidebarCollapsed}
              onClick={() => {
                if (compactViewport) setCompactMenuOpen((current) => !current);
                else
                  setSidebarCollapsed((current) => {
                    sessionStorage.setItem('travel.admin.sidebarCollapsed', String(!current));
                    return !current;
                  });
              }}
            >
              ☰
            </button>
            <strong>{sectionTitle}</strong>
          </div>
          <div className="canonical-topbar__actions">
            <Link className="canonical-bell" href={pathFor('ADM-I02')}>
              <span aria-label="进入消息发送记录">◇</span>
            </Link>
            <button
              type="button"
              className="canonical-help"
              title="显示当前页安全帮助"
              aria-label="显示当前页安全帮助"
              onClick={() => setShowHelp((visible) => !visible)}
            >
              帮助
            </button>
            <span className="canonical-avatar" aria-hidden="true">
              {(runtime.user?.displayName || runtime.user?.id || '用').slice(0, 1)}
            </span>
            <Link className="canonical-account" href="/admin/me/profile" title="个人资料与修改密码">
              <strong>{runtime.user?.displayName || runtime.user?.id || '已登录员工'}</strong>
              <small>个人资料 / 修改密码 · {expiryCopy}</small>
            </Link>
            <ActionButton
              type="button"
              className="canonical-logout"
              pending={loggingOut}
              pendingLabel="退出中…"
              onClick={() => {
                setLoggingOut(true);
                void runtime.logout().finally(() => setLoggingOut(false));
              }}
            >
              退出登录
            </ActionButton>
          </div>
        </header>
        <div className="canonical-content">
          {commandFeedback ? (
            <Notice tone={commandFeedback.tone}>{commandFeedback.message}</Notice>
          ) : null}
          {showHelp && (
            <div className="configuration-error" role="status">
              <strong>当前页帮助</strong>
              <span>默认显示全部授权数据。筛选仅影响当前页面，所有操作仍由服务端验证权限。</span>
            </div>
          )}
          {runtime.configError && (
            <Notice tone="error">
              <strong>登录配置异常</strong>
              <span>当前会话无法安全访问后台，请重新登录或联系管理员。</span>
            </Notice>
          )}
          {PAGE_DATASETS[pageId] &&
            ![
              'ADM-C02',
              'ADM-C03',
              'ADM-C04',
              'ADM-C07',
              'ADM-C09',
              'ADM-C10',
              'ADM-E01',
              'ADM-E03',
              'ADM-E04',
              'ADM-E05',
            ].includes(pageId) && (
              <PageScopeFilter
                api={runtime.api}
                dataset={PAGE_DATASETS[pageId]!}
                scope={runtime.pageScope}
                onChange={runtime.setPageScope}
              />
            )}
          {children}
        </div>
      </main>
    </div>
  );
}
function PageHeader({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="page-header">
      <div>
        <h1>{title}</h1>
        <p>{description}</p>
      </div>
      {children && <div className="button-row">{children}</div>}
    </div>
  );
}
export function StateView({
  state,
  size,
  noun = '数据',
  children,
}: {
  state: LoadState<unknown>;
  size: number;
  noun?: string;
  children: React.ReactNode;
}) {
  const kind = deriveViewKind(state.loading, state.error, size);
  if (kind === 'loading')
    return (
      <div className="state-card">
        <span className="spinner" />
        正在加载业务数据…
      </div>
    );
  if (kind === 'permission')
    return (
      <div className="state-card permission">
        <strong>无权访问</strong>
        <p>当前账号没有查看此内容的权限，请联系管理员确认授权范围。</p>
        <button onClick={state.reload}>重试</button>
      </div>
    );
  if (kind === 'error')
    return (
      <div className="state-card error">
        <strong>加载失败</strong>
        <p>业务数据暂时无法加载，请稍后重试。</p>
        <button onClick={state.reload}>重试</button>
      </div>
    );
  if (kind === 'empty')
    return (
      <div className="state-card">
        <strong>暂无{noun}</strong>
        <button onClick={state.reload}>刷新</button>
      </div>
    );
  return <>{children}</>;
}
function valueDisplay(value: unknown): React.ReactNode {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean')
    return (
      <span className={`badge ${value ? 'success' : 'muted'}`}>{value ? '启用' : '停用'}</span>
    );
  if (typeof value === 'object')
    return <code className="json-inline">{JSON.stringify(value)}</code>;
  return String(value);
}
export function DataTable({
  items,
  columns,
  link,
  actionLabel,
  onAction,
}: {
  items: JsonRecord[];
  columns: Array<[string, string]>;
  link?: (item: JsonRecord) => string | undefined;
  actionLabel?: string;
  onAction?: (item: JsonRecord) => void;
}) {
  if (items.length === 0) {
    return <div className="state-card compact">当前数据集为空</div>;
  }
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            {columns.map(([key, label]) => (
              <th key={key}>{label}</th>
            ))}
            {(link || onAction) && <th>操作</th>}
          </tr>
        </thead>
        <tbody>
          {items.map((item, index) => (
            <tr key={textOf(item, 'id', 'code') + index}>
              {columns.map(([key]) => (
                <td key={key}>{valueDisplay(item[key])}</td>
              ))}
              {link &&
                (() => {
                  const href = link(item);
                  return (
                    <td>
                      {href ? (
                        <Link className="text-link" href={href}>
                          查看
                        </Link>
                      ) : (
                        <span className="muted">无可追溯事件</span>
                      )}
                    </td>
                  );
                })()}
              {!link && onAction && (
                <td>
                  <button className="text-link" onClick={() => onAction(item)}>
                    {actionLabel ?? '处理'}
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
function DetailGrid({ record, fields }: { record: JsonRecord; fields: Array<[string, string]> }) {
  return (
    <div className="detail-grid">
      {fields.map(([key, label]) => (
        <div key={key}>
          <span>{label}</span>
          <strong>{valueDisplay(record[key])}</strong>
        </div>
      ))}
    </div>
  );
}
function MetricGrid({
  items,
}: {
  items: Array<{
    label: string;
    value: React.ReactNode;
    note?: string;
    tone?: 'default' | 'success' | 'warning' | 'danger';
  }>;
}) {
  return (
    <section className="metric-grid" aria-label="关键指标">
      {items.map((item) => (
        <article className={`metric-card tone-${item.tone ?? 'default'}`} key={item.label}>
          <span>{item.label}</span>
          <strong>{item.value}</strong>
          {item.note && <small>{item.note}</small>}
        </article>
      ))}
    </section>
  );
}
function SectionTabs({
  items,
}: {
  items: Array<{ label: string; href: string; active?: boolean }>;
}) {
  return (
    <nav className="section-tabs" aria-label="当前业务对象导航">
      {items.map((item) => (
        <Link className={item.active ? 'active' : ''} href={item.href} key={item.label}>
          {item.label}
        </Link>
      ))}
    </nav>
  );
}
function BarSummary({ items }: { items: Array<{ label: string; value: number; note?: string }> }) {
  const maximum = Math.max(1, ...items.map((item) => item.value));
  if (items.length === 0) return <div className="state-card compact">当前没有可聚合数据</div>;
  return (
    <div className="bar-summary" aria-label="真实数据分布">
      {items.map((item) => (
        <div className="bar-summary__row" key={item.label}>
          <span>{item.label}</span>
          <div className="bar-summary__track">
            <i style={{ width: `${Math.max(3, (item.value / maximum) * 100)}%` }} />
          </div>
          <strong>{item.value}</strong>
          {item.note && <small>{item.note}</small>}
        </div>
      ))}
    </div>
  );
}

const adminOrderStatusLabels: Record<string, string> = {
  PENDING_PAYMENT: '待支付',
  PAID_NEED_TRIP_INFO: '待补行程',
  PAID_INFO_READY: '行程待确认',
  PENDING_CONFIRM: '待运营确认',
  PENDING_FULFILLMENT: '待履约',
  FULFILLMENT_READY: '履约准备完成',
  IN_SERVICE: '服务中',
  COMPLETED: '已完成',
  CANCELLED: '已取消',
  REFUND_APPLIED: '退款申请中',
  REFUND_PROCESSING: '退款处理中',
  REFUNDED: '已退款',
  AFTER_SALES: '售后处理中',
  AFTER_SALES_CLOSED: '售后已关闭',
};

const scopeTypeLabels: Record<string, string> = {
  ALL: '全平台',
  APP: '小程序',
  CITY: '城市',
  PRODUCT: '产品',
  SKU: '套餐',
  CHANNEL: '渠道',
  ORG: '组织',
  ASSIGNED_ORDER: '分配订单',
  SELF: '本人',
};

const fieldAccessLabels: Record<string, string> = {
  ALLOW: '可见',
  MASK: '默认脱敏',
  DENY: '不可见',
  REDACT: '不可返回',
};

const alertStatusLabels: Record<string, string> = {
  OPEN: '待处理',
  ACKNOWLEDGED: '已知悉',
  RESOLVED: '已关闭',
};

const comparatorLabels: Record<string, string> = {
  GT: '高于阈值',
  GTE: '达到或高于阈值',
  LT: '低于阈值',
  LTE: '达到或低于阈值',
  EQ: '等于阈值',
};

const metricLabels: Record<string, string> = {
  'api.availability': '接口可用率',
  'api.error_rate': '接口错误率',
  'order.payment.failure_rate': '订单支付失败率',
  'payment.failure_rate': '支付失败率',
  'outbox.pending': '可靠任务待处理',
  'outbox.failed': '可靠任务失败',
  'message.backlog': '消息任务积压',
  'merchant.health.score': '商户健康分',
};

const permissionResourceLabels: Record<string, string> = {
  order: '订单履约',
  payment: '支付中心',
  member: '会员运营',
  coupon: '优惠券',
  channel: '渠道推广',
  commission: '佣金结算',
  finance: '财务报表',
  cms: '装修发布',
  city: '城市配置',
  product: '产品套餐',
  system: '系统权限',
};

function displayText(value: unknown, fallback = '—') {
  if (value === null || value === undefined) return fallback;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return fallback;
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu.test(trimmed))
      return fallback;
    return trimmed;
  }
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return fallback;
}

function nestedAdmin(record: JsonRecord, key: string): JsonRecord {
  const value = record[key];
  return isRecord(value) ? value : {};
}

function listField(record: JsonRecord, key: string): JsonRecord[] {
  const value = record[key];
  return Array.isArray(value) ? value.filter(isRecord) : [];
}

function orderTripAdmin(order: JsonRecord) {
  return nestedAdmin(order, 'tripInfo');
}

function adminDate(value: unknown, fallback = '—') {
  const text = displayText(value, '');
  if (!text) return fallback;
  const date = new Date(text);
  if (Number.isNaN(date.getTime())) return text.slice(0, 10) || fallback;
  return new Intl.DateTimeFormat('zh-CN', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function adminDateOnly(value: unknown, fallback = '待补充') {
  const text = displayText(value, '');
  if (!text) return fallback;
  return text.includes('T') ? text.slice(0, 10) : text;
}

function adminMoney(value: unknown) {
  const cents = Number(value ?? 0);
  if (!Number.isFinite(cents)) return '¥0.00';
  return `¥${(cents / 100).toLocaleString('zh-CN', { minimumFractionDigits: 2 })}`;
}

function orderStatusText(value: unknown) {
  const raw = displayText(value, '').toUpperCase();
  return adminOrderStatusLabels[raw] ?? displayText(value, '状态待确认');
}

function scopeTypeText(value: unknown) {
  const raw = displayText(value, '').toUpperCase();
  return scopeTypeLabels[raw] ?? '业务范围';
}

function fieldAccessText(value: unknown) {
  const raw = displayText(value, '').toUpperCase();
  return fieldAccessLabels[raw] ?? '按策略处理';
}

function alertStatusText(value: unknown) {
  const raw = displayText(value, '').toUpperCase();
  return alertStatusLabels[raw] ?? '待确认';
}

function metricText(value: unknown) {
  const raw = displayText(value, '');
  return metricLabels[raw] ?? '平台运行指标';
}

function comparatorText(value: unknown) {
  const raw = displayText(value, '').toUpperCase();
  return comparatorLabels[raw] ?? '达到规则阈值';
}

function orderProductText(order: JsonRecord) {
  const firstItem = listField(order, 'items')[0];
  return displayText(
    order.productTitle ?? order.spuTitle ?? firstItem?.productTitle ?? firstItem?.title,
    '综合旅游服务',
  );
}

function orderCityText(order: JsonRecord) {
  return displayText(order.cityName ?? order.sourceCityName, '授权城市');
}

function orderContactText(order: JsonRecord) {
  const trip = orderTripAdmin(order);
  const contact = nestedAdmin(order, 'contact');
  return displayText(
    order.contactName ?? trip.contactName ?? contact.contactName,
    '游客信息按权限显示',
  );
}

function orderTravelDate(order: JsonRecord) {
  const trip = orderTripAdmin(order);
  return adminDateOnly(order.serviceDate ?? order.tripDate ?? trip.travelDate);
}

function orderPeopleText(order: JsonRecord) {
  const trip = orderTripAdmin(order);
  return displayText(order.peopleCount ?? trip.peopleCount, '待补充');
}

function orderSearchText(order: JsonRecord) {
  const trip = orderTripAdmin(order);
  return [
    order.orderNo,
    order.status,
    order.cityName,
    order.sourceCityName,
    orderProductText(order),
    trip.contactName,
    trip.mobile,
    trip.pickupAddress,
  ]
    .map((value) => displayText(value, ''))
    .join(' ')
    .toLocaleLowerCase();
}

function permissionRefs(role: JsonRecord) {
  return listField(role, 'permissions');
}

function permissionName(row: JsonRecord) {
  const configured = displayText(row.description ?? row.name, '');
  if (
    configured &&
    /[\u4e00-\u9fff]/u.test(configured) &&
    !/(?:DEV[/-]?TEST|fixture|synthetic|M\d+\s+permission)/iu.test(configured)
  ) {
    return configured;
  }
  const code = displayText(row.code, '').toLocaleLowerCase();
  if (code.includes('export')) return '数据导出';
  if (code.includes('refund')) return '退款处理';
  if (code.includes('release')) return '发布门禁';
  if (code.includes('settlement')) return '结算审核';
  if (code.includes('manage')) return '配置管理';
  if (code.includes('view'))
    return `${permissionResourceLabels[displayText(row.resource, '')] ?? '业务'}查看`;
  return `${permissionResourceLabels[displayText(row.resource, '')] ?? '业务'}权限`;
}

function auditActorText(value: unknown) {
  const configured = displayText(value, '');
  if (!configured) return '系统审计';
  if (/(?:DEV[/-]?TEST|fixture|synthetic|local fixture|seed)/iu.test(configured)) {
    return '系统审计';
  }
  if (/secure operator CLI/iu.test(configured)) return '安全运维';
  if (!/[\u4e00-\u9fff]/u.test(configured) && configured.length > 24) return '授权操作员';
  return configured;
}

function auditActionText(value: unknown) {
  const raw = displayText(value, '').toLocaleLowerCase();
  if (!raw) return '审计事件';
  if (raw.includes('credential')) return '登录凭据维护';
  if (raw.includes('refund')) return '退款操作';
  if (raw.includes('export')) return '数据导出';
  if (raw.includes('permission') || raw.includes('security') || raw.includes('role'))
    return '权限变更';
  if (raw.includes('merchant') || raw.includes('payment_route')) return '商户配置变更';
  if (raw.includes('release')) return '发布门禁';
  if (raw.includes('settlement')) return '结算审核';
  if (raw.includes('order')) return '订单处理';
  if (raw.includes('configuration') || raw.includes('config')) return '运营配置变更';
  return '审计事件';
}

function auditObjectText(value: unknown) {
  const raw = displayText(value, '').toLocaleUpperCase();
  if (raw.includes('SYS_USER')) return '员工账号';
  if (raw.includes('ORDER')) return '订单';
  if (raw.includes('PAYMENT')) return '支付配置';
  if (raw.includes('SETTLEMENT')) return '结算批次';
  if (raw.includes('RELEASE')) return '发布记录';
  if (raw.includes('M2_CONFIGURATION')) return '小程序配置';
  return '业务对象';
}

function isHighRiskAudit(action: unknown) {
  const raw = displayText(action, '').toLocaleLowerCase();
  return ['refund', 'export', 'permission', 'security', 'merchant', 'release', 'settlement'].some(
    (term) => raw.includes(term),
  );
}

function countByLabel(items: JsonRecord[], labeler: (item: JsonRecord) => string) {
  return items.reduce<Map<string, number>>((map, item) => {
    const label = labeler(item);
    map.set(label, (map.get(label) ?? 0) + 1);
    return map;
  }, new Map());
}

function AdminGlobalSearchPage({
  state,
  keyword,
  setKeyword,
}: {
  state: LoadState<unknown>;
  keyword: string;
  setKeyword: (value: string) => void;
}) {
  const orders = unwrapList(state.data);
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const needle = keyword.trim().toLocaleLowerCase();
  const visibleOrders = orders.filter(
    (order) =>
      (!needle || orderSearchText(order).includes(needle)) &&
      (!status || textOf(order, 'status') === status),
  );
  const pageSize = 14;
  const pageCount = Math.max(1, Math.ceil(visibleOrders.length / pageSize));
  const currentPage = Math.min(page, pageCount);
  return (
    <div className="erp-base-workspace">
      <PageHeader title="全局搜索" description="在授权订单中查找游客、线路、城市和接送点" />
      <section className="erp-base-toolbar">
        <label>
          <span>搜索当前授权范围</span>
          <input
            type="search"
            placeholder="订单号 / 游客 / 线路 / 城市或接送点"
            value={keyword}
            onChange={(event) => {
              setKeyword(event.target.value);
              setPage(1);
            }}
          />
        </label>
        <label>
          <span>订单状态</span>
          <select
            value={status}
            onChange={(event) => {
              setStatus(event.target.value);
              setPage(1);
            }}
          >
            <option value="">全部状态</option>
            {[...new Set(orders.map((order) => textOf(order, 'status')))].map((value) => (
              <option key={value} value={value}>
                {orderStatusText(value)}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          onClick={() => {
            setKeyword('');
            setStatus('');
            setPage(1);
          }}
        >
          重置
        </button>
      </section>
      <StateView state={state} size={orders.length} noun="授权搜索数据">
        <div className="canonical-table-wrap">
          <table className="erp-base-table">
            <thead>
              <tr>
                <th>订单号</th>
                <th>线路产品</th>
                <th>城市</th>
                <th>游客</th>
                <th>出行日期</th>
                <th>人数</th>
                <th>应付</th>
                <th>状态</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {visibleOrders
                .slice((currentPage - 1) * pageSize, currentPage * pageSize)
                .map((order) => (
                  <tr key={textOf(order, 'id')}>
                    <td>{displayText(order.orderNo, '订单号待生成')}</td>
                    <td>{orderProductText(order)}</td>
                    <td>{orderCityText(order)}</td>
                    <td>{orderContactText(order)}</td>
                    <td>{orderTravelDate(order)}</td>
                    <td>{orderPeopleText(order)}</td>
                    <td>{adminMoney(order.payAmountCents ?? order.amountCents)}</td>
                    <td>
                      <span className="badge">{orderStatusText(order.status)}</span>
                    </td>
                    <td>
                      <Link
                        className="text-link"
                        href={pathFor('ADM-E02', { id: displayText(order.id, '') })}
                      >
                        查看订单
                      </Link>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
          {!visibleOrders.length && (
            <p className="state-card compact">当前关键词没有匹配的授权结果。</p>
          )}
          <div className="erp-base-page-footer">
            <span>
              共 {visibleOrders.length} 条 · 每页 {pageSize} 条
            </span>
            <button
              type="button"
              disabled={currentPage <= 1}
              onClick={() => setPage(currentPage - 1)}
            >
              上一页
            </button>
            <span>
              {currentPage} / {pageCount}
            </span>
            <button
              type="button"
              disabled={currentPage >= pageCount}
              onClick={() => setPage(currentPage + 1)}
            >
              下一页
            </button>
          </div>
        </div>
      </StateView>
    </div>
  );
}
function CapabilityNote({ write = false }: { write?: boolean }) {
  return (
    <div className="capability-note">
      <strong>{write ? '受控命令' : '当前只读'}</strong>
      <span>
        {write
          ? '提交会调用现有服务端 API，并由服务端权限、状态机与版本校验决定结果。'
          : '当前服务端 API 未提供此项写命令，本页仅呈现真实数据，不宣称保存能力完成。'}
      </span>
    </div>
  );
}
function JsonCommand({
  title,
  endpoint,
  method,
  onDone,
}: {
  title: string;
  endpoint: string;
  method: 'POST' | 'PUT' | 'PATCH';
  onDone?: () => void;
}) {
  const { api } = useRuntime();
  const [body, setBody] = useState('{}');
  const [result, setResult] = useState<string>();
  const [busy, setBusy] = useState(false);
  async function submit() {
    setBusy(true);
    setResult(undefined);
    try {
      await api.send(endpoint, method, JSON.parse(body) as unknown);
      setResult('命令执行成功');
      onDone?.();
    } catch {
      setResult('操作未完成，请检查填写内容或稍后重试');
    } finally {
      setBusy(false);
    }
  }
  return (
    <details className="command-panel">
      <summary>{title}</summary>
      <CapabilityNote write />
      <textarea
        aria-label={`${title} JSON`}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        spellCheck={false}
      />
      <div className="button-row">
        <button className="primary" disabled={busy} onClick={() => void submit()}>
          {busy ? '提交中…' : '提交命令'}
        </button>
        {result && <span className="command-result">{result}</span>}
      </div>
    </details>
  );
}

function CityScopedCommand({
  title,
  endpointFor,
  method,
  onDone,
}: {
  title: string;
  endpointFor: (cityId: string) => string;
  method: 'POST' | 'PUT';
  onDone?: () => void;
}) {
  const cities = useGet('/api/admin/m2/cities');
  const items = unwrapList(cities.data);
  const [cityId, setCityId] = useState('');
  return (
    <section className="card">
      <label>
        选择城市资源
        <select value={cityId} onChange={(event) => setCityId(event.target.value)}>
          <option value="">请选择</option>
          {items.map((city) => (
            <option key={textOf(city, 'id')} value={textOf(city, 'id')}>
              {textOf(city, 'name', 'code')}
            </option>
          ))}
        </select>
      </label>
      {cityId ? (
        <JsonCommand
          title={title}
          endpoint={endpointFor(cityId)}
          method={method}
          {...(onDone ? { onDone } : {})}
        />
      ) : (
        <CapabilityNote write />
      )}
    </section>
  );
}
const commonColumns: Array<[string, string]> = [
  ['name', '名称'],
  ['code', '编码'],
  ['status', '状态'],
  ['id', 'ID'],
];
export const SKU_TABLE_COLUMNS: Array<[string, string]> = [
  ['skuName', '套餐'],
  ['skuCode', '编码'],
  ['serviceMode', '服务模式'],
  ['pricingMode', '计价方式'],
  ['ticketMode', '票务方式'],
  ['guideMode', '向导方式'],
  ['salePriceCents', '售价（分）'],
  ['status', '状态'],
];
export const TEMPLATE_COLUMNS: Array<[string, string]> = [
  ['name', '模板'],
  ['templateCode', '模板编码'],
  ['version', '版本'],
  ['description', '说明'],
  ['status', '状态'],
  ['id', 'ID'],
];
export const DEFINITION_COLUMNS: Array<[string, string]> = [
  ['displayName', '组件'],
  ['componentKey', '组件键'],
  ['status', '状态'],
];

function CityPages({ pageId, params }: { pageId: string; params: Params }) {
  const runtime = useRuntime();
  const isList = pageId === 'ADM-B01';
  const path = isList ? '/api/admin/m2/cities' : `/api/admin/m2/cities/${params.id}`;
  const state = useGet(path);
  const items = isList
    ? unwrapList(state.data)
    : [unwrapRecord(state.data)].filter((item) => Object.keys(item).length);
  if (pageId === 'ADM-B01') {
    return <CanonicalCityListPage api={runtime.api} targetAppId={runtime.targetAppId} />;
  }
  if (isList)
    return (
      <>
        <PageHeader title="城市管理" description="城市用于组织线路的可售范围；停用保留历史关联。" />
        <MetricGrid
          items={[
            { label: '城市总数', value: items.length, note: '来自城市 API' },
            {
              label: '启用城市',
              value: items.filter((item) => textOf(item, 'status') === 'ACTIVE').length,
              note: '可进入后续售卖配置',
              tone: 'success',
            },
            {
              label: '停用城市',
              value: items.filter((item) => textOf(item, 'status') !== 'ACTIVE').length,
              note: '历史关联仍保留',
              tone: 'warning',
            },
            { label: '销售管理', value: '每天可售', note: '暂停销售请下架线路' },
          ]}
        />
        <section className="card">
          <StateView state={state} size={items.length} noun="城市">
            <DataTable
              items={items}
              columns={commonColumns}
              link={(item) => pathFor('ADM-B02', { id: textOf(item, 'id') })}
            />
          </StateView>
        </section>
        {runtime.targetAppId ? (
          <section className="card">
            <CityCreateForm api={runtime.api} appId={runtime.targetAppId} onDone={state.reload} />
            <details className="advanced-command">
              <summary>高级 JSON 命令</summary>
              <JsonCommand
                title="创建城市"
                endpoint={`/api/admin/m2/apps/${runtime.targetAppId}/cities`}
                method="POST"
                onDone={state.reload}
              />
            </details>
          </section>
        ) : (
          <CapabilityNote />
        )}
      </>
    );
  const city = items[0] ?? {};
  const pickups = Array.isArray(city.pickupAreas) ? city.pickupAreas.filter(isRecord) : [];
  const pois = Array.isArray(city.pois) ? city.pois.filter(isRecord) : [];
  const cityTabs = [
    {
      label: '基础信息',
      href: pathFor('ADM-B02', { id: params.id ?? '' }),
      active: pageId === 'ADM-B02',
    },
    {
      label: '服务区域与景点',
      href: pathFor('ADM-B03', { id: params.id ?? '' }),
      active: pageId === 'ADM-B03',
    },
  ];
  if (pageId === 'ADM-B03')
    return (
      <>
        <PageHeader
          title="服务区域与景点"
          description="接送范围与地点来自城市 API，仅用于定义服务覆盖边界。"
        />
        <SectionTabs items={cityTabs} />
        <MetricGrid
          items={[
            { label: '接送区域', value: pickups.length, note: '服务覆盖边界' },
            { label: 'POI', value: pois.length, note: '真实地点配置' },
            { label: '城市状态', value: textOf(city, 'status'), tone: 'success' },
            { label: '城市编码', value: textOf(city, 'code') },
          ]}
        />
        <section className="split">
          <div className="card">
            <h2>接送区域</h2>
            <StateView state={state} size={items.length}>
              <DataTable items={pickups} columns={commonColumns} />
            </StateView>
            <JsonCommand
              title="新增或更新接送区域"
              endpoint={`/api/admin/m2/cities/${params.id}/pickup-areas`}
              method="PUT"
              onDone={state.reload}
            />
          </div>
          <div className="card">
            <h2>POI</h2>
            <StateView state={state} size={items.length}>
              <DataTable items={pois} columns={commonColumns} />
            </StateView>
            <JsonCommand
              title="新增或更新 POI"
              endpoint={`/api/admin/m2/cities/${params.id}/pois`}
              method="PUT"
              onDone={state.reload}
            />
          </div>
        </section>
      </>
    );
  return (
    <>
      <PageHeader title="城市详情" description="查看城市基础配置与历史安全停用命令。">
        <Link className="button" href={pathFor('ADM-B03', { id: params.id ?? '' })}>
          服务区域与景点
        </Link>
      </PageHeader>
      <SectionTabs items={cityTabs} />
      <MetricGrid
        items={[
          { label: '城市状态', value: textOf(city, 'status'), tone: 'success' },
          { label: '接送区域', value: pickups.length, note: '服务覆盖边界' },
          { label: 'POI', value: pois.length, note: '地点配置' },
          { label: '时区', value: textOf(city, 'timezone') },
        ]}
      />
      <section className="card">
        <StateView state={state} size={items.length}>
          <DetailGrid
            record={city}
            fields={commonColumns.concat([
              ['timezone', '时区'],
              ['regionId', '地区 ID'],
              ['sortOrder', '排序'],
            ])}
          />
        </StateView>
      </section>
      <JsonCommand
        title="停用城市（无需请求体）"
        endpoint={`/api/admin/m2/cities/${params.id}/disable`}
        method="PATCH"
        onDone={state.reload}
      />
    </>
  );
}

function AppProducts({ appId }: { appId: string }) {
  const state = useGet(`/api/admin/m2/products?appId=${encodeURIComponent(appId)}`);
  const items = unwrapList(state.data);
  return (
    <>
      <PageHeader title="小程序产品覆盖" description="仅展示当前 App 授权与覆盖后的产品集合。" />
      <section className="card">
        <StateView state={state} size={items.length} noun="产品">
          <DataTable
            items={items}
            columns={[
              ['title', '产品'],
              ['productCode', '编码'],
              ['cityId', '城市'],
              ['status', '状态'],
            ]}
            link={(item) => pathFor('ADM-C09', { id: textOf(item, 'id') })}
          />
        </StateView>
      </section>
    </>
  );
}
function AppStatusPage() {
  const { api } = useRuntime();
  const health = usePublicGet('/api/health');
  const appsState = useGet('/api/admin/m2/apps');
  const apps = unwrapList(appsState.data);
  const appIds = apps.map((app) => textOf(app, 'id')).filter((id) => id !== '—');
  const detailsState = useAsync(
    `app-status:${appIds.join(',')}`,
    async () =>
      Promise.all(
        appIds.map(async (appId) => {
          const detail = unwrapRecord(await api.get(`/api/admin/m2/apps/${appId}`));
          const app = isRecord(detail.app) ? detail.app : {};
          const runtime = isRecord(detail.runtime) ? detail.runtime : {};
          const cities = Array.isArray(detail.cities) ? detail.cities : [];
          return {
            id: appId,
            displayName: textOf(app, 'displayName'),
            appKey: textOf(app, 'appKey'),
            appStatus: textOf(app, 'status'),
            runtimeVersion: runtime.version ?? '—',
            defaultCityId: runtime.defaultCityId ?? '—',
            configuredCities: cities.length,
            configStatus: Object.keys(runtime).length ? 'CONFIGURED' : 'NOT_CONFIGURED',
          } satisfies JsonRecord;
        }),
      ),
    api,
  );
  const details = Array.isArray(detailsState.data) ? detailsState.data : [];
  const healthRecord = unwrapRecord(health.data);
  const configuredCount = details.filter(
    (detail) => textOf(detail, 'configStatus') === 'CONFIGURED',
  ).length;
  return (
    <>
      <PageHeader
        title="小程序运行与配置状态"
        description="组合公开 API 健康状态、授权 App 清单以及逐 App 运行配置，不复用矩阵列表结论。"
      />
      <MetricGrid
        items={[
          { label: '授权 App', value: apps.length, note: '服务端授权范围' },
          {
            label: '启用 App',
            value: apps.filter((app) => textOf(app, 'status') === 'ACTIVE').length,
            tone: 'success',
          },
          { label: '已配置运行时', value: configuredCount, note: '逐 App 查询确认' },
          {
            label: 'API 健康',
            value: textOf(healthRecord, 'status'),
            tone: textOf(healthRecord, 'status') === 'ok' ? 'success' : 'danger',
          },
        ]}
      />
      <section className="status-grid">
        <div className="card">
          <h2>API 服务</h2>
          <StateView state={health} size={Object.keys(healthRecord).length} noun="健康状态">
            <DetailGrid
              record={healthRecord}
              fields={[
                ['status', '状态'],
                ['service', '服务'],
                ['timestamp', '时间'],
              ]}
            />
          </StateView>
        </div>
        <div className="card">
          <h2>App 授权范围</h2>
          <StateView state={appsState} size={apps.length} noun="小程序">
            <strong className="metric">{apps.length}</strong>
            <span className="muted-copy">个可访问 App</span>
          </StateView>
        </div>
      </section>
      <section className="card">
        <h2>逐 App 运行配置</h2>
        <StateView state={detailsState} size={details.length} noun="App 配置">
          <DataTable
            items={details}
            columns={[
              ['displayName', '小程序'],
              ['appKey', 'App Key'],
              ['appStatus', 'App 状态'],
              ['configStatus', '配置状态'],
              ['runtimeVersion', '配置版本'],
              ['defaultCityId', '默认城市'],
              ['configuredCities', '已配城市数'],
            ]}
            link={(item) => pathFor('ADM-B05', { id: textOf(item, 'id') })}
          />
        </StateView>
      </section>
    </>
  );
}
// Kept temporarily for routes outside the canonical remediation diff history.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function AppPages({ pageId, params }: { pageId: string; params: Params }) {
  const runtimeContext = useRuntime();
  const isList = pageId === 'ADM-B04' || pageId === 'ADM-B09';
  const appId = params.id;
  const state = useGet(isList ? '/api/admin/m2/apps' : `/api/admin/m2/apps/${appId}`);
  const items = isList
    ? unwrapList(state.data)
    : [unwrapRecord(state.data)].filter((item) => Object.keys(item).length);
  if (pageId === 'ADM-B09') return <AppStatusPage />;
  if (isList)
    return (
      <>
        <PageHeader
          title="多小程序矩阵"
          description="App 配置以 app_id 隔离；微信 AppID 引用不等于真实微信验证。"
        />
        <MetricGrid
          items={[
            { label: '小程序总数', value: items.length, note: '当前授权范围' },
            {
              label: '启用中',
              value: items.filter((item) => textOf(item, 'status') === 'ACTIVE').length,
              tone: 'success',
            },
            {
              label: '测试 Fixture',
              value: items.filter((item) => item.isFixture === true).length,
              note: '不等于真实微信验证',
              tone: 'warning',
            },
            { label: '隔离维度', value: 'app_id', note: '覆盖与配置独立' },
          ]}
        />
        <section className="card">
          <StateView state={state} size={items.length} noun="小程序">
            <DataTable
              items={items}
              columns={[
                ['displayName', '名称'],
                ['appKey', 'App Key'],
                ['status', '状态'],
                ['wechatAppIdRef', '微信 AppID 引用'],
                ['environment', '环境'],
                ['isFixture', 'Fixture'],
                ['id', '内部 ID'],
              ]}
              link={(item) => pathFor('ADM-B05', { id: textOf(item, 'id') })}
            />
          </StateView>
        </section>
      </>
    );
  const rawApp = items[0] ?? {};
  const app = isRecord(rawApp.app) ? { ...rawApp.app, ...rawApp } : rawApp;
  const runtime = isRecord(app.runtimeConfig)
    ? app.runtimeConfig
    : isRecord(app.runtime)
      ? app.runtime
      : {};
  const cities: JsonRecord[] = Array.isArray(app.cities)
    ? app.cities.filter(isRecord).map((entry) => {
        const city = isRecord(entry.city) ? entry.city : {};
        return {
          ...entry,
          cityName: city.name,
          cityCode: city.code,
          cityStatus: city.status,
        };
      })
    : [];
  const appTabs = [
    {
      label: '运行配置',
      href: pathFor('ADM-B05', { id: appId ?? '' }),
      active: pageId === 'ADM-B05',
    },
    {
      label: '城市覆盖',
      href: pathFor('ADM-B06', { id: appId ?? '' }),
      active: pageId === 'ADM-B06',
    },
    {
      label: '产品覆盖',
      href: pathFor('ADM-B07', { id: appId ?? '' }),
      active: pageId === 'ADM-B07',
    },
    {
      label: '客服配置',
      href: pathFor('ADM-B08', { id: appId ?? '' }),
      active: pageId === 'ADM-B08',
    },
  ];
  if (pageId === 'ADM-B06')
    return (
      <>
        <PageHeader
          title="城市覆盖"
          description="绑定关系决定该小程序可展示的城市，不删除城市历史数据。"
        />
        <SectionTabs items={appTabs} />
        <MetricGrid
          items={[
            { label: '已绑定城市', value: cities.length, note: '当前 App 范围' },
            {
              label: '启用覆盖',
              value: cities.filter((city) => city['enabled'] === true).length,
              tone: 'success',
            },
            {
              label: '停用覆盖',
              value: cities.filter((city) => city['enabled'] !== true).length,
              tone: 'warning',
            },
            { label: '隔离键', value: 'app_id', note: textOf(app, 'appKey') },
          ]}
        />
        <section className="card">
          <StateView state={state} size={items.length}>
            <DataTable
              items={cities}
              columns={[
                ['cityName', '城市'],
                ['cityCode', '编码'],
                ['enabled', '覆盖启用'],
                ['cityStatus', '城市状态'],
                ['sortOrder', '排序'],
              ]}
            />
          </StateView>
        </section>
        <CityScopedCommand
          title="绑定选中城市"
          endpointFor={(cityId) => `/api/admin/m2/apps/${appId ?? ''}/cities/${cityId}`}
          method="PUT"
          onDone={state.reload}
        />
      </>
    );
  if (pageId === 'ADM-B07')
    return (
      <>
        <SectionTabs items={appTabs} />
        <AppProducts appId={appId ?? ''} />
      </>
    );
  const customerService = isRecord(runtime.customerServiceConfig)
    ? runtime.customerServiceConfig
    : {};
  const configRecord = pageId === 'ADM-B08' ? customerService : runtime;
  const configFields: Array<[string, string]> =
    pageId === 'ADM-B08'
      ? [
          ['servicePhone', '客服电话'],
          ['serviceWechat', '客服微信'],
          ['serviceHours', '服务时间'],
          ['contactHint', '联系提示'],
        ]
      : [
          ['version', '配置版本'],
          ['defaultCityId', '默认城市'],
          ['themeConfig', '主题变量'],
        ];
  return (
    <>
      <PageHeader
        title={pageId === 'ADM-B08' ? '客服配置' : '小程序配置'}
        description="主题、客服与运行配置保持 App 级隔离。"
      />
      <SectionTabs items={appTabs} />
      <MetricGrid
        items={[
          { label: 'App 状态', value: textOf(app, 'status'), tone: 'success' },
          { label: '绑定城市', value: cities.length, note: '当前 App 覆盖' },
          { label: '配置版本', value: valueDisplay(runtime.version), note: '服务端运行配置' },
          {
            label: '验证状态',
            value: app.isFixture === true ? 'DEV / TEST' : '待外部确认',
            note: '不伪造真实微信验证',
            tone: 'warning',
          },
        ]}
      />
      <section className="config-panels">
        <div className="card">
          <h2>小程序身份</h2>
          <StateView state={state} size={items.length}>
            <DetailGrid
              record={app}
              fields={[
                ['displayName', '名称'],
                ['appKey', 'App Key'],
                ['status', '状态'],
                ['environment', '环境'],
                ['wechatAppIdRef', '微信 AppID 引用'],
                ['id', '内部 ID'],
              ]}
            />
          </StateView>
        </div>
        <div className="card">
          <h2>{pageId === 'ADM-B08' ? '客服配置摘要' : '运行配置摘要'}</h2>
          <StateView state={state} size={items.length}>
            <DetailGrid record={configRecord} fields={configFields} />
          </StateView>
        </div>
      </section>
      <section className="card">
        <RuntimeConfigForm
          api={runtimeContext.api}
          appId={appId ?? ''}
          initial={runtime}
          onDone={state.reload}
        />
        <details className="advanced-command">
          <summary>高级 JSON 命令</summary>
          <JsonCommand
            title="更新运行配置"
            endpoint={`/api/admin/m2/apps/${appId}/runtime`}
            method="PUT"
            onDone={state.reload}
          />
        </details>
      </section>
    </>
  );
}

function ProductListPage({ pageId }: { pageId: string }) {
  const runtime = useRuntime();
  if (pageId === 'ADM-C01') {
    return <CanonicalProductListPage api={runtime.api} targetAppId={runtime.targetAppId} />;
  }
  return <LegacyProductListPage pageId={pageId} />;
}

function LegacyProductListPage({ pageId }: { pageId: string }) {
  const state = useGet('/api/admin/m2/products');
  const items = unwrapList(state.data);
  const skuCount = items.reduce(
    (total, product) => total + productSubrecord(product, 'skus').length,
    0,
  );
  const productMetrics = (
    <MetricGrid
      items={[
        { label: 'SPU', value: items.length, note: '真实产品主体' },
        { label: 'SKU 套餐', value: skuCount, note: '动态服务套餐' },
        {
          label: '启用产品',
          value: items.filter((item) => textOf(item, 'status') === 'ACTIVE').length,
          tone: 'success',
        },
        {
          label: '覆盖城市',
          value: new Set(items.map((item) => textOf(item, 'cityId'))).size,
          note: '由 API 数据汇总',
        },
      ]}
    />
  );
  if (pageId === 'ADM-C08')
    return (
      <>
        <PageHeader
          title="标签与排序"
          description="按真实产品关联汇总标签；当前 API 未提供标签写命令。"
        />
        {productMetrics}
        <CapabilityNote />
        <section className="card">
          <StateView state={state} size={items.length} noun="产品">
            <DataTable
              items={items}
              columns={[
                ['title', '产品'],
                ['tags', '标签'],
                ['status', '状态'],
                ['id', 'ID'],
              ]}
            />
          </StateView>
        </section>
      </>
    );
  return (
    <>
      <PageHeader
        title="产品管理"
        description="SPU 是产品主体，SKU 是动态服务套餐；页面不使用固定车型、人数或价格。"
      />
      {productMetrics}
      <section className="card">
        <StateView state={state} size={items.length} noun="产品">
          <DataTable
            items={items}
            columns={[
              ['title', '产品名称'],
              ['productCode', '产品编码'],
              ['cityId', '城市'],
              ['status', '状态'],
              ['id', 'ID'],
            ]}
            link={(item) => pathFor('ADM-C02', { id: textOf(item, 'id') })}
          />
        </StateView>
      </section>
      <CityScopedCommand
        title="在选中城市创建 SPU"
        endpointFor={(cityId) => `/api/admin/m2/cities/${cityId}/products`}
        method="POST"
        onDone={state.reload}
      />
    </>
  );
}
function productSubrecord(product: JsonRecord, key: string): JsonRecord[] {
  const value = product[key];
  return Array.isArray(value) ? value.filter(isRecord) : [];
}
function AppCoveragePanel({
  appId,
  productId,
  onChanged,
}: {
  appId: string;
  productId: string;
  onChanged: () => void;
}) {
  const state = useGet(`/api/admin/m2/products?appId=${encodeURIComponent(appId)}`);
  const products = unwrapList(state.data);
  const exposed = products.find((item) => textOf(item, 'id') === productId);
  const summary: JsonRecord = exposed
    ? {
        status: 'EXPOSED',
        appId,
        productId,
        effectiveTitle: exposed.title,
        effectiveSortOrder: exposed.sortOrder,
      }
    : { status: 'NOT_EXPOSED', appId, productId };
  return (
    <>
      <StateView state={state} size={1} noun="目标 App 覆盖状态">
        <DetailGrid
          record={summary}
          fields={[
            ['status', '目标 App 状态'],
            ['appId', '目标 App'],
            ['productId', '产品'],
            ['effectiveTitle', '生效标题'],
            ['effectiveSortOrder', '生效排序'],
          ]}
        />
      </StateView>
      <JsonCommand
        title="更新目标 App 产品覆盖"
        endpoint={`/api/admin/m2/apps/${appId}/products/${productId}/override`}
        method="PUT"
        onDone={() => {
          state.reload();
          onChanged();
        }}
      />
    </>
  );
}
function ProductReadinessPanel({ appId, productId }: { appId: string; productId: string }) {
  const state = useGet(
    `/api/admin/m2/apps/${encodeURIComponent(appId)}/products/${encodeURIComponent(productId)}/publish-readiness`,
  );
  const readiness = unwrapRecord(state.data);
  const checks = Array.isArray(readiness.checks) ? readiness.checks.filter(isRecord) : [];
  return (
    <StateView state={state} size={Object.keys(readiness).length} noun="服务端发布检查">
      <div className={`readiness-summary ${readiness.publishable === true ? 'ready' : 'blocked'}`}>
        <strong>
          {readiness.publishable === true ? '服务端判定：可发布' : '服务端判定：不可发布'}
        </strong>
        <span>检查时间：{textOf(readiness, 'evaluatedAt')}</span>
      </div>
      <DataTable
        items={checks}
        columns={[
          ['code', '检查项'],
          ['passed', '通过'],
          ['reasonCode', '服务端原因码'],
        ]}
      />
    </StateView>
  );
}
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function ProductDetailPage({ pageId, productId }: { pageId: string; productId: string }) {
  const runtime = useRuntime();
  const state = useGet(`/api/admin/m2/products/${productId}`);
  const product = unwrapRecord(state.data);
  const exists = Object.keys(product).length ? 1 : 0;
  const skus = productSubrecord(product, 'skus');
  const routeStops = productSubrecord(product, 'routeStops');
  const productTabs = [
    {
      label: '基础信息',
      href: pathFor('ADM-C02', { id: productId }),
      active: pageId === 'ADM-C02',
    },
    {
      label: '行程配置',
      href: pathFor('ADM-C03', { id: productId }),
      active: pageId === 'ADM-C03',
    },
    {
      label: 'SKU 套餐',
      href: pathFor('ADM-C04', { id: productId }),
      active: pageId === 'ADM-C04',
    },
    {
      label: '规则',
      href: pathFor('ADM-C07', { id: productId }),
      active: pageId === 'ADM-C07',
    },
    {
      label: 'App 覆盖',
      href: pathFor('ADM-C09', { id: productId }),
      active: pageId === 'ADM-C09',
    },
    {
      label: '发布检查',
      href: pathFor('ADM-C10', { id: productId }),
      active: pageId === 'ADM-C10',
    },
  ];
  const serviceModes = new Set(skus.map((sku) => textOf(sku, 'serviceMode'))).size;
  const pricingModes = new Set(skus.map((sku) => textOf(sku, 'pricingMode'))).size;
  let title = '产品基础信息';
  let content: React.ReactNode = (
    <DetailGrid
      record={product}
      fields={[
        ['title', '标题'],
        ['productCode', '编码'],
        ['cityId', '城市'],
        ['status', '状态'],
        ['summary', '摘要'],
        ['bookingNotice', '预订须知'],
      ]}
    />
  );
  if (pageId === 'ADM-C03') {
    title = '行程配置';
    content = (
      <DataTable
        items={routeStops}
        columns={[
          ['name', '站点'],
          ['description', '说明'],
          ['stayMinutes', '停留分钟'],
        ]}
      />
    );
  }
  if (pageId === 'ADM-C04') {
    title = 'SKU 套餐';
    content = (
      <>
        <div className="no-stock">
          服务日期规则只决定某日是否可预订；不承载车辆、票务或座位数量配额。
        </div>
        <DataTable
          items={skus}
          columns={SKU_TABLE_COLUMNS}
          link={(item) => pathFor('ADM-C05', { id: textOf(item, 'id') })}
        />
        <SkuCreateForm api={runtime.api} productId={productId} onDone={state.reload} />
        <details className="advanced-command">
          <summary>高级 JSON 命令</summary>
          <JsonCommand
            title="创建 SKU 套餐"
            endpoint={`/api/admin/m2/products/${productId}/skus`}
            method="POST"
            onDone={state.reload}
          />
        </details>
      </>
    );
  }
  if (pageId === 'ADM-C07') {
    title = '售价与预订';
    content = <p>默认每天可售，停售请下架线路；退款申请由客服人工审核。</p>;
  }
  if (pageId === 'ADM-C09') {
    title = '小程序覆盖';
    content = (
      <>
        {runtime.targetAppId ? (
          <AppCoveragePanel
            appId={runtime.targetAppId}
            productId={productId}
            onChanged={state.reload}
          />
        ) : (
          <div className="state-card">请选择目标小程序。</div>
        )}
      </>
    );
  }
  if (pageId === 'ADM-C10') {
    title = '发布检查';
    content = (
      <>
        {runtime.targetAppId ? (
          <ProductReadinessPanel appId={runtime.targetAppId} productId={productId} />
        ) : (
          <div className="state-card permission">
            未选择目标 App，服务端发布检查未执行，因此不可发布。
          </div>
        )}
      </>
    );
  }
  return (
    <>
      <PageHeader title={title} description="所有内容来自产品详情 API；未开放的编辑能力保持只读。">
        <Link className="button" href={pathFor('ADM-C04', { id: productId })}>
          SKU 套餐
        </Link>
      </PageHeader>
      <SectionTabs items={productTabs} />
      <MetricGrid
        items={[
          { label: '产品状态', value: textOf(product, 'status'), tone: 'success' },
          { label: 'SKU 套餐', value: skus.length, note: '动态服务组合' },
          { label: '服务模式', value: serviceModes, note: '由 SKU 汇总' },
          { label: '计价模式', value: pricingModes, note: '由 SKU 汇总' },
        ]}
      />
      <section className="card">
        <StateView state={state} size={exists} noun="产品">
          {content}
        </StateView>
      </section>
      {!['ADM-C04', 'ADM-C09'].includes(pageId) && <CapabilityNote />}
    </>
  );
}
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function SkuPage({ pageId, skuId }: { pageId: string; skuId: string }) {
  const { api } = useRuntime();
  const state = useAsync(
    `sku:${skuId}`,
    async () => {
      const products = unwrapList(await api.get('/api/admin/m2/products'));
      const details = await Promise.all(
        products.map((product) => api.get(`/api/admin/m2/products/${textOf(product, 'id')}`)),
      );
      for (const detail of details.map(unwrapRecord)) {
        const sku = productSubrecord(detail, 'skus').find((item) => textOf(item, 'id') === skuId);
        if (sku) return sku;
      }
      return {};
    },
    api,
  );
  const sku = unwrapRecord(state.data),
    exists = Object.keys(sku).length ? 1 : 0;
  const services = productSubrecord(sku, 'serviceItems');
  const skuTabs = [
    {
      label: 'SKU 详情',
      href: pathFor('ADM-C05', { id: skuId }),
      active: pageId === 'ADM-C05',
    },
    {
      label: '服务项配置',
      href: pathFor('ADM-C06', { id: skuId }),
      active: pageId === 'ADM-C06',
    },
  ];
  return (
    <>
      <PageHeader
        title={pageId === 'ADM-C06' ? '服务项配置' : 'SKU 详情'}
        description="通过已授权产品集合查找 SKU；未提供直接 SKU 写接口。"
      />
      <SectionTabs items={skuTabs} />
      <MetricGrid
        items={[
          { label: 'SKU 状态', value: textOf(sku, 'status'), tone: 'success' },
          { label: '服务模式', value: textOf(sku, 'serviceMode'), note: '动态 SKU 数据' },
          { label: '计价方式', value: textOf(sku, 'pricingMode') },
          { label: '服务项', value: services.length, note: '服务组合明细' },
        ]}
      />
      <CapabilityNote />
      <section className="card">
        <StateView state={state} size={exists} noun="SKU">
          {pageId === 'ADM-C06' ? (
            <DataTable
              items={services}
              columns={[
                ['name', '服务项'],
                ['category', '分类'],
                ['included', '是否包含'],
                ['description', '说明'],
              ]}
            />
          ) : (
            <DetailGrid
              record={sku}
              fields={[
                ['skuName', '名称'],
                ['skuCode', '编码'],
                ['serviceMode', '服务模式'],
                ['pricingMode', '计价方式'],
                ['ticketMode', '票务方式'],
                ['guideMode', '向导方式'],
                ['salePriceCents', '售价（分）'],
                ['status', '状态'],
              ]}
            />
          )}
        </StateView>
      </section>
    </>
  );
}

function useCmsData(pageId?: string) {
  const { targetAppId } = useRuntime();
  const base = targetAppId ? `/api/admin/m2/apps/${targetAppId}/cms` : undefined;
  return {
    targetAppId,
    base,
    pages: useGet(base ? `${base}/pages` : undefined),
    templates: useGet(base ? `${base}/templates` : undefined),
    definitions: useGet(base ? `${base}/definitions` : undefined),
    versions: useGet(base && pageId ? `${base}/pages/${pageId}/versions` : undefined),
  };
}
function CmsReleases({
  pagesState,
  pages,
  appId,
}: {
  pagesState: LoadState<unknown>;
  pages: JsonRecord[];
  appId: string;
}) {
  const [pageId, setPageId] = useState('');
  const versions = useGet(
    pageId ? `/api/admin/m2/apps/${appId}/cms/pages/${pageId}/versions` : undefined,
  );
  const items = unwrapList(versions.data);
  return (
    <>
      <PageHeader title="发布版本" description="页面版本可发布、可回滚并按 App 隔离。" />
      <section className="card">
        <StateView state={pagesState} size={pages.length} noun="页面">
          <label>
            页面实例
            <select value={pageId} onChange={(e) => setPageId(e.target.value)}>
              <option value="">请选择</option>
              {pages.map((page) => (
                <option key={textOf(page, 'id')} value={textOf(page, 'id')}>
                  {textOf(page, 'pageCode', 'name')}
                </option>
              ))}
            </select>
          </label>
        </StateView>
        {pageId && (
          <StateView state={versions} size={items.length} noun="版本">
            <DataTable
              items={items}
              columns={[
                ['versionNumber', '版本'],
                ['status', '状态'],
                ['publishedAt', '发布时间'],
                ['id', '版本 ID'],
              ]}
              link={(item) => pathFor('ADM-D10', { id: textOf(item, 'id') })}
            />
          </StateView>
        )}
      </section>
    </>
  );
}
export function CmsComponentCards({
  snapshot,
  bindingContext = {},
}: {
  snapshot: unknown;
  bindingContext?: CmsBindingContext;
}) {
  const components = readSafeCmsComponents(snapshot, bindingContext);
  if (components.length === 0) return <div className="phone-empty">当前版本没有组件</div>;
  return (
    <div className="phone-canvas" data-testid="cms-phone-canvas">
      {components.map((component, index) => {
        const props = isRecord(component.props) ? component.props : {};
        const actions = Array.isArray(component.actionIds) ? component.actionIds.map(String) : [];
        const imageUrl =
          typeof props.imageUrl === 'string' && /^(https?:\/\/|\/)/.test(props.imageUrl)
            ? props.imageUrl
            : undefined;
        return (
          <article
            className={`cms-component ${component.componentKey}`}
            key={component.instanceKey + index}
          >
            {imageUrl && <img src={imageUrl} alt="" />}
            <div>
              <small>
                {component.componentKey} · {component.instanceKey}
              </small>
              <h4>{textOf(props, 'title', 'name', 'heading')}</h4>
              {textOf(props, 'subtitle', 'description') !== '—' && (
                <p>{textOf(props, 'subtitle', 'description')}</p>
              )}
              {actions.length > 0 && (
                <div className="action-chips">
                  {actions.map((action) => (
                    <span key={action}>{action}</span>
                  ))}
                </div>
              )}
            </div>
          </article>
        );
      })}
    </div>
  );
}
function snapshotUsesProductBindings(snapshot: unknown) {
  if (!isRecord(snapshot) || !Array.isArray(snapshot.components)) return false;
  return snapshot.components.filter(isRecord).some((component) => {
    if (!isRecord(component.dataBindings)) return false;
    return Object.values(component.dataBindings).some(
      (binding) => typeof binding === 'string' && binding.startsWith('product.'),
    );
  });
}
function useCmsPreviewBindingContext(
  appId: string,
  page: JsonRecord | undefined,
  snapshot: unknown,
) {
  const appState = useGet(appId ? `/api/admin/m2/apps/${appId}` : undefined);
  const pageProductId = page ? textOf(page, 'productSpuId') : '—';
  const needsProduct = snapshotUsesProductBindings(snapshot);
  const needsPreviewProduct = needsProduct && pageProductId === '—';
  const previewProductsState = useGet(
    needsPreviewProduct && appId
      ? `/api/admin/m2/products?appId=${encodeURIComponent(appId)}`
      : undefined,
  );
  const previewProducts = unwrapList(previewProductsState.data);
  const [selectedPreviewProductId, setSelectedPreviewProductId] = useState('');
  const selectedIsExposed = previewProducts.some(
    (product) => textOf(product, 'id') === selectedPreviewProductId,
  );
  const productId =
    pageProductId !== '—'
      ? pageProductId
      : selectedIsExposed
        ? selectedPreviewProductId
        : textOf(previewProducts[0] ?? {}, 'id');
  const productState = useGet(
    productId !== '—' ? `/api/admin/m2/products/${encodeURIComponent(productId)}` : undefined,
  );
  const detail = unwrapRecord(appState.data);
  const runtime = isRecord(detail.runtime) ? detail.runtime : {};
  const cityId = page ? textOf(page, 'cityId') : '—';
  const cityEntry = Array.isArray(detail.cities)
    ? detail.cities.filter(isRecord).find((entry) => {
        const city = isRecord(entry.city) ? entry.city : entry;
        return textOf(city, 'id') === cityId;
      })
    : undefined;
  const city = cityEntry ? (isRecord(cityEntry.city) ? cityEntry.city : cityEntry) : undefined;
  const product = unwrapRecord(productState.data);
  const context: CmsBindingContext = {
    app: {
      theme: runtime.themeConfig,
      customerService: runtime.customerServiceConfig,
    },
    ...(city && cityId !== '—' ? { city: { id: cityId, name: textOf(city, 'name') } } : {}),
    ...(Object.keys(product).length
      ? {
          product: {
            id: textOf(product, 'id'),
            title: textOf(product, 'title'),
            subtitle: typeof product.subtitle === 'string' ? product.subtitle : null,
            summary: typeof product.summary === 'string' ? product.summary : null,
            coverMediaUrl: typeof product.coverMediaUrl === 'string' ? product.coverMediaUrl : null,
            media: product.media,
            routeStops: product.routeStops,
            skus: product.skus,
            tags: product.tags,
          },
        }
      : {}),
  };
  return {
    context,
    appState,
    productState,
    previewProductsState,
    previewProducts,
    productId,
    needsPreviewProduct,
    setSelectedPreviewProductId,
  };
}
function CmsPreviewContext({
  preview,
}: {
  preview: ReturnType<typeof useCmsPreviewBindingContext>;
}) {
  const product = isRecord(preview.context.product) ? preview.context.product : {};
  if (preview.needsPreviewProduct)
    return (
      <div className="preview-context" data-testid="cms-preview-context">
        <StateView
          state={preview.previewProductsState}
          size={preview.previewProducts.length}
          noun="目标 App 可预览产品"
        >
          <label>
            预览产品上下文
            <select
              value={preview.productId === '—' ? '' : preview.productId}
              onChange={(event) => preview.setSelectedPreviewProductId(event.target.value)}
            >
              {preview.previewProducts.map((item) => (
                <option key={textOf(item, 'id')} value={textOf(item, 'id')}>
                  {textOf(item, 'title')} · {textOf(item, 'id')}
                </option>
              ))}
            </select>
          </label>
          <small>
            当前上下文：{textOf(product, 'title')} · {preview.productId}
          </small>
        </StateView>
      </div>
    );
  if (preview.productId !== '—')
    return (
      <div className="preview-context" data-testid="cms-preview-context">
        <strong>页面绑定产品上下文</strong>
        <small>
          {textOf(product, 'title')} · {preview.productId}
        </small>
      </div>
    );
  return null;
}
function CmsBoundComponentCards({
  snapshot,
  preview,
}: {
  snapshot: unknown;
  preview: ReturnType<typeof useCmsPreviewBindingContext>;
}) {
  if (snapshotUsesProductBindings(snapshot))
    return (
      <StateView
        state={preview.productState}
        size={isRecord(preview.context.product) ? 1 : 0}
        noun="预览产品详情"
      >
        <CmsComponentCards snapshot={snapshot} bindingContext={preview.context} />
      </StateView>
    );
  return <CmsComponentCards snapshot={snapshot} bindingContext={preview.context} />;
}
function CmsPreview({
  appId,
  pages,
  pagesState,
}: {
  appId: string;
  pages: JsonRecord[];
  pagesState: LoadState<unknown>;
}) {
  const [pageId, setPageId] = useState('');
  const effectivePageId = pages.some((page) => textOf(page, 'id') === pageId)
    ? pageId
    : textOf(pages[0] ?? {}, 'id');
  const versions = useGet(
    effectivePageId !== '—'
      ? `/api/admin/m2/apps/${appId}/cms/pages/${effectivePageId}/versions`
      : undefined,
  );
  const latest = unwrapList(versions.data)[0];
  const snapshot = latest && isRecord(latest.snapshot) ? latest.snapshot : {};
  const selectedPage = pages.find((page) => textOf(page, 'id') === effectivePageId);
  const preview = useCmsPreviewBindingContext(appId, selectedPage, snapshot);
  return (
    <>
      <PageHeader title="页面预览" description="预览真实版本快照，不执行受控 Action。" />
      <section className="preview-layout">
        <div className="card">
          <StateView state={pagesState} size={pages.length}>
            <label>
              页面
              <select
                value={effectivePageId === '—' ? '' : effectivePageId}
                onChange={(e) => setPageId(e.target.value)}
              >
                <option value="">请选择</option>
                {pages.map((page) => (
                  <option key={textOf(page, 'id')} value={textOf(page, 'id')}>
                    {textOf(page, 'pageCode')}
                  </option>
                ))}
              </select>
            </label>
          </StateView>
        </div>
        <div className="phone-preview large">
          <span>小程序预览</span>
          {effectivePageId !== '—' && <CmsPreviewContext preview={preview} />}
          {effectivePageId !== '—' ? (
            <StateView state={versions} size={unwrapList(versions.data).length} noun="发布版本">
              <CmsBoundComponentCards snapshot={snapshot} preview={preview} />
            </StateView>
          ) : (
            <div className="phone-empty">请选择页面</div>
          )}
        </div>
      </section>
    </>
  );
}
function CmsDiff({
  releaseId,
  appId,
  pages,
  pagesState,
}: {
  releaseId: string;
  appId: string;
  pages: JsonRecord[];
  pagesState: LoadState<unknown>;
}) {
  const { api } = useRuntime();
  const state = useAsync(
    `diff:${appId}:${releaseId}:${pages.length}`,
    async () => {
      for (const page of pages) {
        const pageId = textOf(page, 'id');
        const versions = unwrapList(
          await api.get(`/api/admin/m2/apps/${appId}/cms/pages/${pageId}/versions`),
        );
        const target = versions.find((version) => textOf(version, 'id') === releaseId);
        if (target) return { pageId, target, current: versions[0] ?? {} };
      }
      return {};
    },
    api,
  );
  const diff = unwrapRecord(state.data),
    found = Object.keys(diff).length ? 1 : 0;
  const target = isRecord(diff.target) ? diff.target : {},
    current = isRecord(diff.current) ? diff.current : {};
  return (
    <>
      <PageHeader
        title="版本差异"
        description="对比目标版本与当前版本快照；回滚由服务端创建新版本。"
      />
      <section className="card">
        <StateView state={pagesState} size={pages.length}>
          <StateView state={state} size={found} noun="版本">
            <div className="split">
              <div>
                <h3>目标版本</h3>
                <pre>{JSON.stringify(target.snapshot ?? target, null, 2)}</pre>
              </div>
              <div>
                <h3>当前版本</h3>
                <pre>{JSON.stringify(current.snapshot ?? current, null, 2)}</pre>
              </div>
            </div>
            {Boolean(diff.pageId && target.versionNumber) && (
              <JsonCommand
                title="回滚到目标版本"
                endpoint={`/api/admin/m2/apps/${appId}/cms/pages/${String(diff.pageId)}/rollback/${String(target.versionNumber)}`}
                method="POST"
                onDone={state.reload}
              />
            )}
          </StateView>
        </StateView>
      </section>
    </>
  );
}
// Preserved only for incremental migration history; production routes use canonical-round03.
export function LegacyCmsPage({ pageId, params }: { pageId: string; params: Params }) {
  const runtime = useRuntime();
  const data = useCmsData(params.pageId);
  const pages = unwrapList(data.pages.data);
  const selectedPage = pages.find((page) => textOf(page, 'id') === params.pageId);
  const templates = unwrapList(data.templates.data),
    definitions = unwrapList(data.definitions.data),
    versions = unwrapList(data.versions.data);
  const latest = versions[0] ?? {};
  const snapshot = isRecord(latest.snapshot) ? latest.snapshot : {};
  const preview = useCmsPreviewBindingContext(data.targetAppId, selectedPage, snapshot);
  if (!data.targetAppId)
    return (
      <>
        <PageHeader title="CMS 内容" description="CMS 数据必须按 app_id 隔离。" />
        <div className="state-card">请先在顶部选择目标小程序。</div>
      </>
    );
  if (pageId === 'ADM-D01')
    return (
      <>
        <PageHeader title="页面模板" description="Schema Driven、组件白名单、受控 Action ID。" />
        <MetricGrid
          items={[
            { label: '页面模板', value: templates.length, note: 'Schema Driven' },
            { label: '页面实例', value: pages.length, note: '当前 App 隔离范围' },
            {
              label: '组件定义',
              value: definitions.length,
              note: '白名单组件',
              tone: 'success',
            },
            {
              label: '已发布页面',
              value: pages.filter((page) => Number(page.currentPublishedVersionNumber ?? 0) > 0)
                .length,
              note: '版本化发布',
            },
          ]}
        />
        <section className="card">
          <h2>模板</h2>
          <StateView state={data.templates} size={templates.length} noun="模板">
            <DataTable items={templates} columns={TEMPLATE_COLUMNS} />
          </StateView>
        </section>
        <section className="card">
          <h2>页面实例</h2>
          <StateView state={data.pages} size={pages.length} noun="页面">
            <DataTable
              items={pages}
              columns={[
                ['pageCode', '页面编码'],
                ['scopeType', '范围'],
                ['status', '状态'],
                ['id', 'ID'],
              ]}
              link={(item) => pathFor('ADM-D02', { pageId: textOf(item, 'id') })}
            />
          </StateView>
        </section>
        <section className="card">
          <CmsPageCreateForm
            api={runtime.api}
            appId={data.targetAppId}
            templates={templates}
            onDone={data.pages.reload}
          />
          <details className="advanced-command">
            <summary>高级 JSON 命令</summary>
            <JsonCommand
              title="创建页面实例"
              endpoint={`${data.base}/pages`}
              method="POST"
              onDone={data.pages.reload}
            />
          </details>
        </section>
      </>
    );
  if (pageId === 'ADM-D06' || pageId === 'ADM-D07') {
    const scope = pageId === 'ADM-D06' ? 'CITY' : 'PRODUCT';
    const scoped = pages.filter((page) => textOf(page, 'scopeType') === scope);
    return (
      <>
        <PageHeader
          title={pageId === 'ADM-D06' ? '城市覆盖页' : '产品详情模板'}
          description="覆盖层级由服务端实例范围决定。"
        />
        <MetricGrid
          items={[
            { label: '覆盖页面', value: scoped.length, note: `${scope} 范围` },
            {
              label: '启用页面',
              value: scoped.filter((page) => textOf(page, 'status') === 'ACTIVE').length,
              tone: 'success',
            },
            { label: '模板总数', value: templates.length },
            { label: '隔离维度', value: 'app_id', note: '服务端强制' },
          ]}
        />
        <section className="card">
          <StateView state={data.pages} size={scoped.length} noun="覆盖页面">
            <DataTable
              items={scoped}
              columns={[
                ['pageCode', '页面编码'],
                ['scopeKey', '范围键'],
                ['status', '状态'],
                ['id', 'ID'],
              ]}
              link={(item) => pathFor('ADM-D02', { pageId: textOf(item, 'id') })}
            />
          </StateView>
        </section>
      </>
    );
  }
  if (pageId === 'ADM-D09')
    return <CmsReleases pagesState={data.pages} pages={pages} appId={data.targetAppId} />;
  if (pageId === 'ADM-D10')
    return (
      <CmsDiff
        releaseId={params.id ?? ''}
        appId={data.targetAppId}
        pages={pages}
        pagesState={data.pages}
      />
    );
  if (pageId === 'ADM-D08')
    return <CmsPreview appId={data.targetAppId} pages={pages} pagesState={data.pages} />;
  const components = Array.isArray(snapshot.components) ? snapshot.components.filter(isRecord) : [];
  const cmsTabs = params.pageId
    ? [
        {
          label: '可视化编辑器',
          href: pathFor('ADM-D02', { pageId: params.pageId }),
          active: pageId === 'ADM-D02',
        },
        {
          label: '组件属性',
          href: pathFor('ADM-D03', { pageId: params.pageId }),
          active: pageId === 'ADM-D03',
        },
        {
          label: '页面图层',
          href: pathFor('ADM-D04', { pageId: params.pageId }),
          active: pageId === 'ADM-D04',
        },
        {
          label: '受控动作',
          href: pathFor('ADM-D05', { pageId: params.pageId }),
          active: pageId === 'ADM-D05',
        },
      ]
    : [];
  const heading =
    (
      {
        'ADM-D02': '可视化编辑器',
        'ADM-D03': '组件属性',
        'ADM-D04': '页面图层',
        'ADM-D05': '受控动作',
      } as Record<string, string>
    )[pageId] ?? 'CMS 编辑';
  let body: React.ReactNode = (
    <div className="editor-grid">
      <div>
        <h3>组件白名单</h3>
        <DataTable items={definitions} columns={DEFINITION_COLUMNS} />
      </div>
      <div className="phone-preview">
        <span>页面画布</span>
        <CmsPreviewContext preview={preview} />
        <CmsBoundComponentCards snapshot={snapshot} preview={preview} />
      </div>
      <div>
        <h3>版本信息</h3>
        <DetailGrid
          record={latest}
          fields={[
            ['versionNumber', '版本'],
            ['status', '状态'],
            ['publishedAt', '发布时间'],
          ]}
        />
      </div>
    </div>
  );
  if (pageId === 'ADM-D03')
    body = (
      <div className="editor-grid">
        <div>
          <h3>当前组件</h3>
          <DataTable
            items={components}
            columns={[
              ['instanceKey', '实例键'],
              ['componentKey', '组件'],
              ['sortOrder', '排序'],
            ]}
          />
        </div>
        <div className="phone-preview">
          <span>属性修改预览</span>
          <CmsPreviewContext preview={preview} />
          <CmsBoundComponentCards snapshot={snapshot} preview={preview} />
        </div>
        <div className="component-property-list">
          <h3>属性面板</h3>
          {components.map((component) => {
            const props = isRecord(component.props)
              ? component.props
              : isRecord(component.properties)
                ? component.properties
                : {};
            const fields = Object.keys(props).map((key) => [key, key] satisfies [string, string]);
            return (
              <article key={textOf(component, 'instanceKey', 'componentKey')}>
                <strong>{textOf(component, 'componentKey')}</strong>
                {fields.length ? (
                  <DetailGrid record={props} fields={fields} />
                ) : (
                  <span>暂无可配置属性</span>
                )}
              </article>
            );
          })}
        </div>
      </div>
    );
  if (pageId === 'ADM-D04')
    body = (
      <div className="editor-grid">
        <div>
          <h3>页面图层</h3>
          <div className="layer-list">
            {components.map((component, index) => (
              <article key={textOf(component, 'instanceKey', 'componentKey')}>
                <span>{index + 1}</span>
                <div>
                  <strong>{textOf(component, 'componentKey')}</strong>
                  <small>{textOf(component, 'instanceKey')}</small>
                </div>
                <em>{valueDisplay(component.sortOrder)}</em>
              </article>
            ))}
          </div>
        </div>
        <div className="phone-preview">
          <span>图层实时预览</span>
          <CmsPreviewContext preview={preview} />
          <CmsBoundComponentCards snapshot={snapshot} preview={preview} />
        </div>
        <div>
          <h3>层级摘要</h3>
          <DetailGrid
            record={{
              componentCount: components.length,
              pageCode: snapshot.pageCode,
              scopeType: snapshot.scopeType,
              version: latest.versionNumber,
            }}
            fields={[
              ['componentCount', '组件数'],
              ['pageCode', '页面编码'],
              ['scopeType', '范围'],
              ['version', '版本'],
            ]}
          />
        </div>
      </div>
    );
  if (pageId === 'ADM-D05')
    body = (
      <>
        <CapabilityNote />
        <div className="action-flow">
          {definitions.map((definition) => {
            const actions = Array.isArray(definition.allowedActionIds)
              ? definition.allowedActionIds
              : [];
            return (
              <article key={textOf(definition, 'componentKey')}>
                <div>
                  <small>白名单组件</small>
                  <strong>{textOf(definition, 'displayName')}</strong>
                  <span>{textOf(definition, 'componentKey')}</span>
                </div>
                <b>→</b>
                <div>
                  <small>受控 Action ID</small>
                  <strong>{actions.length ? actions.map(String).join(' · ') : '无动作'}</strong>
                  <span>{textOf(definition, 'status')}</span>
                </div>
              </article>
            );
          })}
        </div>
      </>
    );
  return (
    <>
      <PageHeader
        title={heading}
        description="任意 JS、HTML script、API URL 与任意支付/退款调用均不在编辑能力中。"
      />
      <SectionTabs items={cmsTabs} />
      <MetricGrid
        items={[
          { label: '页面状态', value: textOf(selectedPage ?? {}, 'status'), tone: 'success' },
          { label: '当前版本', value: valueDisplay(latest.versionNumber), note: '版本快照' },
          { label: '页面组件', value: components.length, note: '白名单实例' },
          { label: '页面范围', value: textOf(selectedPage ?? {}, 'scopeType') },
        ]}
      />
      <section className="card">
        <StateView state={data.versions} size={versions.length} noun="版本">
          {body}
        </StateView>
      </section>
      {params.pageId && (
        <>
          <section className="card">
            <CmsComponentForm
              api={runtime.api}
              appId={data.targetAppId}
              pageId={params.pageId}
              definitions={definitions}
              expectedDraftVersion={Number(selectedPage?.draftVersion ?? 0)}
              onDone={data.versions.reload}
            />
            <details className="advanced-command">
              <summary>高级 JSON 命令</summary>
              <JsonCommand
                title="添加白名单组件"
                endpoint={`${data.base}/pages/${params.pageId}/components`}
                method="POST"
                onDone={data.versions.reload}
              />
            </details>
          </section>
          <JsonCommand
            title="发布页面"
            endpoint={`${data.base}/pages/${params.pageId}/publish`}
            method="POST"
            onDone={data.versions.reload}
          />
        </>
      )}
    </>
  );
}

function NotFound() {
  return (
    <div className="state-card">
      <strong>页面不存在</strong>
      <p>当前路径未登记在 M2 Page-ID 注册表中。</p>
      <Link className="button" href="/admin/cities">
        返回城市管理
      </Link>
    </div>
  );
}

function m4CommandKey(prefix: string) {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}
function M4Command({
  title,
  endpoint,
  method,
  initialBody,
  onDone,
  idempotent = true,
}: {
  title: string;
  endpoint: string;
  method: 'POST' | 'PUT';
  initialBody: string;
  onDone?: () => void;
  idempotent?: boolean;
}) {
  const { api } = useRuntime();
  const [body, setBody] = useState(initialBody);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState('');
  const key = React.useRef(m4CommandKey('admin-m4'));
  async function submit() {
    if (busy) return;
    setBusy(true);
    setResult('');
    try {
      const request: RequestInit = {
        method,
        headers: idempotent ? { 'Idempotency-Key': key.current } : {},
      };
      if (body.trim()) request.body = JSON.stringify(JSON.parse(body) as unknown);
      await api.request(endpoint, request);
      key.current = m4CommandKey('admin-m4');
      setResult('服务端命令执行成功');
      onDone?.();
    } catch {
      setResult('操作未完成，请检查填写内容或稍后重试');
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="card command-panel">
      <h2>{title}</h2>
      <CapabilityNote write />
      <textarea value={body} onChange={(event) => setBody(event.target.value)} />
      <div className="button-row">
        <button className="primary" disabled={busy} onClick={() => void submit()}>
          {busy ? '提交中…' : '提交服务端命令'}
        </button>
        {result && <span className="command-result">{result}</span>}
      </div>
    </section>
  );
}
function M4Unavailable({ title, reason }: { title: string; reason: string }) {
  return (
    <>
      <PageHeader title={title} description="本页不使用静态业务数据或前端伪状态。" />
      <div className="state-card empty">
        <strong>服务端能力尚未提供</strong>
        <span>{reason}</span>
      </div>
    </>
  );
}
function nextM4OrderCommand(status: string) {
  return (
    {
      PAID_INFO_READY: 'SUBMIT_FOR_CONFIRMATION',
      PENDING_CONFIRM: 'CONFIRM_ORDER',
      CONFIRMED: 'START_FULFILLMENT_PREPARATION',
      PENDING_FULFILLMENT: 'MARK_FULFILLMENT_READY',
      FULFILLMENT_READY: 'START_SERVICE',
      IN_SERVICE: 'COMPLETE_SERVICE',
    } as Record<string, string>
  )[status];
}
function nextAfterSalesStatus(status: string) {
  return (
    (
      {
        OPEN: 'PROCESSING',
        PROCESSING: 'RESOLVED',
        RESOLVED: 'CLOSED',
      } as Record<string, string>
    )[status] ?? ''
  );
}
function RefundReviewPanel({
  refund,
  appId,
  onDone,
}: {
  refund: JsonRecord;
  appId: string;
  onDone: () => void;
}) {
  const { api } = useRuntime();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState('');
  const key = React.useRef(m4CommandKey('refund-review'));
  async function review(decision: 'APPROVE' | 'REJECT') {
    if (busy) return;
    setBusy(true);
    setResult('');
    try {
      await api.request(
        `/api/admin/m4/refunds/${encodeURIComponent(textOf(refund, 'id'))}/review?appId=${encodeURIComponent(appId)}`,
        {
          method: 'POST',
          headers: { 'Idempotency-Key': key.current },
          body: JSON.stringify({
            expectedVersion: Number(refund.version ?? 0),
            decision,
            ...(reason.trim() ? { reason: reason.trim() } : {}),
          }),
        },
      );
      key.current = m4CommandKey('refund-review');
      setResult(decision === 'APPROVE' ? '审核已批准' : '审核已拒绝');
      onDone();
    } catch {
      setResult('退款审核未完成，请稍后重试');
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="card command-panel">
      <h2>处理退款申请 {textOf(refund, 'id')}</h2>
      <label>
        审核说明
        <input value={reason} onChange={(event) => setReason(event.target.value)} />
      </label>
      <div className="button-row">
        <button className="primary" disabled={busy} onClick={() => void review('APPROVE')}>
          批准
        </button>
        <button disabled={busy} onClick={() => void review('REJECT')}>
          拒绝
        </button>
        {result && <span className="command-result">{result}</span>}
      </div>
      <CapabilityNote write />
    </section>
  );
}

function FulfillmentEditor({
  order,
  appId,
  onDone,
}: {
  order: JsonRecord;
  appId: string;
  onDone: () => void;
}) {
  const { api } = useRuntime();
  const fulfillment = isRecord(order.fulfillment) ? order.fulfillment : {};
  const remoteChecklist = unwrapList(fulfillment.checklist);
  const [checklist, setChecklist] = useState(remoteChecklist);
  const [serviceRemark, setServiceRemark] = useState(textOf(fulfillment, 'serviceRemark'));
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState('');
  const key = useRef(m4CommandKey('fulfillment'));
  useEffect(() => {
    setChecklist(remoteChecklist);
    setServiceRemark(textOf(fulfillment, 'serviceRemark'));
  }, [order.id, fulfillment.version]);
  async function submit() {
    if (busy) return;
    setBusy(true);
    setResult('');
    try {
      await api.request(
        `/api/admin/m4/orders/${encodeURIComponent(textOf(order, 'id'))}/fulfillment?appId=${encodeURIComponent(appId)}`,
        {
          method: 'PUT',
          headers: { 'Idempotency-Key': key.current },
          body: JSON.stringify({
            expectedVersion: Number(fulfillment.version ?? 0),
            serviceRemark,
            checklist: checklist.map((item) => ({
              code: textOf(item, 'code'),
              completed: item.completed === true,
              ...(textOf(item, 'note') ? { note: textOf(item, 'note') } : {}),
            })),
          }),
        },
      );
      key.current = m4CommandKey('fulfillment');
      setResult('履约回填已保存');
      onDone();
    } catch {
      setResult('履约信息保存失败，请稍后重试');
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="card command-panel">
      <h2>更新履约 Checklist</h2>
      {checklist.length === 0 && <p>请先执行 START_FULFILLMENT_PREPARATION 生成服务端清单。</p>}
      {checklist.map((item, index) => (
        <label key={textOf(item, 'code')}>
          <span>
            <input
              type="checkbox"
              checked={item.completed === true}
              onChange={(event) =>
                setChecklist((current) =>
                  current.map((candidate, candidateIndex) =>
                    candidateIndex === index
                      ? { ...candidate, completed: event.target.checked }
                      : candidate,
                  ),
                )
              }
            />
            {textOf(item, 'code')}
            {item.required === true ? '（必填）' : ''}
          </span>
          <input
            value={textOf(item, 'note')}
            placeholder="此项备注（可选）"
            onChange={(event) =>
              setChecklist((current) =>
                current.map((candidate, candidateIndex) =>
                  candidateIndex === index ? { ...candidate, note: event.target.value } : candidate,
                ),
              )
            }
          />
        </label>
      ))}
      <label>
        运营备注
        <input value={serviceRemark} onChange={(event) => setServiceRemark(event.target.value)} />
      </label>
      <div className="button-row">
        <button
          className="primary"
          disabled={busy || checklist.length === 0}
          onClick={() => void submit()}
        >
          {busy ? '保存中…' : '保存履约回填'}
        </button>
        {result && <span className="command-result">{result}</span>}
      </div>
      <CapabilityNote write />
    </section>
  );
}
function M4AdminPage({ pageId, params }: { pageId: string; params: Params }) {
  const runtime = useRuntime();
  const appId = runtime.targetAppId;
  const [selectedRecordId, setSelectedRecordId] = useState(params.id ?? '');
  useEffect(() => setSelectedRecordId(params.id ?? ''), [pageId, params.id]);
  const unavailable = !appId;
  const status = pageId === 'ADM-E05' ? 'PENDING_CONFIRM' : undefined;
  const orderEndpoint =
    appId && pageId !== 'ADM-E08' && pageId !== 'ADM-G09'
      ? `/api/admin/m4/orders?appId=${encodeURIComponent(appId)}${status ? `&status=${status}` : ''}`
      : undefined;
  const orders = useGet(orderEndpoint);
  const communications = useGet(
    appId && pageId === 'ADM-E07' && params.id
      ? `/api/admin/m4/orders/${encodeURIComponent(params.id)}/communications?appId=${encodeURIComponent(appId)}`
      : undefined,
  );
  const refunds = useGet(
    appId && (pageId === 'ADM-E08' || pageId === 'ADM-G09')
      ? `/api/admin/m4/refunds?appId=${encodeURIComponent(appId)}`
      : undefined,
  );
  const refundAccess = useGet(pageId === 'ADM-E08' ? '/api/admin/m8/security/me' : undefined);
  const refundPermissionCodes = unwrapRecord(refundAccess.data).permissionCodes;
  const canProcessRefund =
    Array.isArray(refundPermissionCodes) && refundPermissionCodes.includes('refund.process');
  const templates = useGet(
    appId && pageId === 'ADM-I01'
      ? `/api/admin/m4/message-templates?appId=${encodeURIComponent(appId)}`
      : undefined,
  );
  const tasks = useGet(
    appId && pageId === 'ADM-I02'
      ? `/api/admin/m4/message-tasks?appId=${encodeURIComponent(appId)}`
      : undefined,
  );
  const afterSales = useGet(
    appId && pageId === 'ADM-E09'
      ? `/api/admin/m4/after-sales?appId=${encodeURIComponent(appId)}`
      : undefined,
  );
  const subscriptions = useGet(
    appId && pageId === 'ADM-I03'
      ? `/api/admin/m4/message-subscriptions?appId=${encodeURIComponent(appId)}`
      : undefined,
  );
  if (unavailable)
    return <M4Unavailable title="M4 运营能力" reason="请先选择目标小程序后读取 App 隔离数据。" />;
  if (pageId === 'ADM-E09') {
    const items = unwrapList(afterSales.data);
    const ticket = items.find((item) => textOf(item, 'id') === selectedRecordId);
    const nextTicketStatus = ticket ? nextAfterSalesStatus(textOf(ticket, 'status')) : '';
    return (
      <>
        <PageHeader title="售后工单" description="工单、沟通及状态变更均写入服务端审计链路。" />
        <MetricGrid
          items={[
            { label: '工单总数', value: items.length, note: '当前 App' },
            {
              label: '待处理',
              value: items.filter((item) => ['OPEN', 'PROCESSING'].includes(textOf(item, 'status')))
                .length,
              tone: 'warning',
            },
            {
              label: '已解决',
              value: items.filter((item) => textOf(item, 'status') === 'RESOLVED').length,
              tone: 'success',
            },
            { label: '当前选中', value: ticket ? textOf(ticket, 'ticketNo', 'id') : '未选择' },
          ]}
        />
        <section className="card">
          <StateView state={afterSales} size={items.length} noun="售后工单">
            <DataTable
              items={items}
              columns={[
                ['ticketNo', '工单号'],
                ['orderId', '订单'],
                ['subject', '主题'],
                ['status', '状态'],
                ['assignedTo', '负责人'],
              ]}
              actionLabel="处理此工单"
              onAction={(item) => setSelectedRecordId(textOf(item, 'id'))}
            />
          </StateView>
        </section>
        {ticket && (
          <>
            <M4Command
              title={`新增工单沟通 · ${textOf(ticket, 'ticketNo', 'id')}`}
              endpoint={`/api/admin/m4/after-sales/${encodeURIComponent(textOf(ticket, 'id'))}/communications?appId=${encodeURIComponent(appId)}`}
              method="POST"
              initialBody={'{"summary":""}'}
              idempotent={false}
              onDone={afterSales.reload}
            />
            <M4Command
              title={`记录人工补偿审计说明 · ${textOf(ticket, 'ticketNo', 'id')}`}
              endpoint={`/api/admin/m4/after-sales/${encodeURIComponent(textOf(ticket, 'id'))}/compensation-notes?appId=${encodeURIComponent(appId)}`}
              method="POST"
              initialBody={'{"summary":""}'}
              idempotent={false}
              onDone={afterSales.reload}
            />
            {nextTicketStatus && (
              <M4Command
                title={`更新工单状态至 ${nextTicketStatus} · ${textOf(ticket, 'ticketNo', 'id')}`}
                endpoint={`/api/admin/m4/after-sales/${encodeURIComponent(textOf(ticket, 'id'))}/transitions?appId=${encodeURIComponent(appId)}`}
                method="POST"
                initialBody={JSON.stringify({
                  expectedVersion: Number(ticket.version ?? 0),
                  status: nextTicketStatus,
                })}
                onDone={afterSales.reload}
              />
            )}
          </>
        )}
      </>
    );
  }
  if (pageId === 'ADM-I03') {
    const items = unwrapList(subscriptions.data).map((item) => ({
      ...item,
      templateCode: isRecord(item.template) ? textOf(item.template, 'code') : '—',
    }));
    return (
      <>
        <PageHeader title="用户订阅状态" description="订阅状态来自服务端 App 隔离记录。" />
        <section className="card">
          <StateView state={subscriptions} size={items.length} noun="订阅状态">
            <DataTable
              items={items}
              columns={[
                ['customerOwnerKey', '会员身份引用'],
                ['templateCode', '模板'],
                ['status', '状态'],
                ['updatedAt', '更新时间'],
              ]}
            />
          </StateView>
        </section>
      </>
    );
  }
  if (pageId === 'ADM-E05') {
    const items = unwrapList(orders.data);
    return (
      <>
        <PageHeader title="待运营确认订单" description="只读取服务端 PENDING_CONFIRM 订单。" />
        <SectionTabs
          items={[
            { label: '全部订单', href: pathFor('ADM-E01') },
            { label: '待支付', href: pathFor('ADM-E03') },
            { label: '待补资料', href: pathFor('ADM-E04') },
            { label: '待确认', href: pathFor('ADM-E05'), active: true },
          ]}
        />
        <MetricGrid
          items={[
            { label: '待确认', value: items.length, note: 'PENDING_CONFIRM', tone: 'warning' },
            {
              label: '标准订单',
              value: items.filter((item) => textOf(item, 'orderMode') === 'STANDARD').length,
            },
            {
              label: '快捷订单',
              value: items.filter((item) => textOf(item, 'orderMode') === 'QUICK').length,
            },
            { label: '下一命令', value: 'CONFIRM_ORDER', note: '服务端状态机校验' },
          ]}
        />
        <section className="card">
          <StateView state={orders} size={items.length} noun="待确认订单">
            <DataTable
              items={items}
              columns={[
                ['orderNo', '订单号'],
                ['status', '状态'],
                ['sourceCityId', '城市'],
                ['version', '版本'],
              ]}
              link={(item) => pathFor('ADM-E06', { id: textOf(item, 'id') })}
            />
          </StateView>
        </section>
      </>
    );
  }
  if (pageId === 'ADM-E06' || pageId === 'ADM-E07') {
    const order = unwrapList(orders.data).find((item) => textOf(item, 'id') === params.id) ?? {};
    const id = params.id ?? '';
    const nextCommand = nextM4OrderCommand(textOf(order, 'status'));
    const fulfillment = isRecord(order.fulfillment) ? order.fulfillment : {};
    const communicationItems = unwrapList(communications.data);
    return (
      <>
        <PageHeader
          title={pageId === 'ADM-E06' ? '人工履约回填' : '订单沟通日志'}
          description="订单和命令均来自 M4 API；服务端执行权限、Scope、版本和状态机校验。"
        />
        <SectionTabs
          items={[
            { label: '订单详情', href: pathFor('ADM-E02', { id }) },
            { label: '人工履约', href: pathFor('ADM-E06', { id }), active: pageId === 'ADM-E06' },
            { label: '沟通记录', href: pathFor('ADM-E07', { id }), active: pageId === 'ADM-E07' },
          ]}
        />
        <MetricGrid
          items={[
            { label: '订单状态', value: textOf(order, 'status'), tone: 'warning' },
            { label: '订单版本', value: valueDisplay(order.version), note: '乐观锁版本' },
            {
              label: '履约状态',
              value: textOf(fulfillment, 'status'),
              note: '服务端履约快照',
            },
            {
              label: pageId === 'ADM-E07' ? '沟通记录' : '下一命令',
              value: pageId === 'ADM-E07' ? communicationItems.length : (nextCommand ?? '无'),
            },
          ]}
        />
        <section className="card">
          <StateView state={orders} size={Object.keys(order).length} noun="订单">
            <DetailGrid
              record={order}
              fields={[
                ['orderNo', '订单号'],
                ['status', '状态'],
                ['version', '订单版本'],
                ['tripInfo', '行程资料'],
                ['fulfillment', '履约快照'],
              ]}
            />
          </StateView>
        </section>
        {pageId === 'ADM-E06' ? (
          <>
            {nextCommand && (
              <M4Command
                title={`执行状态命令 · ${nextCommand}`}
                endpoint={`/api/admin/m4/orders/${encodeURIComponent(id)}/transitions?appId=${encodeURIComponent(appId)}`}
                method="POST"
                initialBody={JSON.stringify({
                  command: nextCommand,
                  expectedVersion: Number(order.version ?? 0),
                })}
                onDone={orders.reload}
              />
            )}
            <FulfillmentEditor order={order} appId={appId} onDone={orders.reload} />
          </>
        ) : (
          <>
            <section className="card">
              <h2>历史沟通记录</h2>
              <StateView state={communications} size={communicationItems.length} noun="沟通记录">
                <DataTable
                  items={communicationItems}
                  columns={[
                    ['createdAt', '时间'],
                    ['channel', '渠道'],
                    ['direction', '方向'],
                    ['summary', '摘要'],
                  ]}
                />
              </StateView>
            </section>
            <M4Command
              title="新增沟通记录"
              endpoint={`/api/admin/m4/orders/${encodeURIComponent(id)}/communications?appId=${encodeURIComponent(appId)}`}
              method="POST"
              initialBody={'{"channel":"PHONE","direction":"OUTBOUND","summary":""}'}
              idempotent={false}
              onDone={communications.reload}
            />
          </>
        )}
      </>
    );
  }
  if (pageId === 'ADM-E08' || pageId === 'ADM-G09') {
    const items = unwrapList(refunds.data);
    const selectedRefund = items.find((item) => textOf(item, 'id') === selectedRecordId);
    const ledgerItems = items.map((item) => ({
      ...item,
      orderNo: textOf(unwrapRecord(item.order), 'orderNo', textOf(item, 'orderId')),
      refundAmount:
        typeof item.amountCents === 'number' ? `¥${(item.amountCents / 100).toFixed(2)}` : '—',
    }));
    const loaded = !refunds.loading && !refunds.error;
    return (
      <>
        <PageHeader
          title={pageId === 'ADM-E08' ? '退款申请处理' : '退款流水'}
          description={
            pageId === 'ADM-E08'
              ? '审核退款申请，查看处理进度。资金操作需单独授权。'
              : '查看授权范围内的退款金额、状态和对应订单。'
          }
        />
        <MetricGrid
          items={[
            { label: '退款记录', value: loaded ? items.length : '—', note: '当前授权范围' },
            {
              label: '待审核',
              value: loaded
                ? items.filter((item) => textOf(item, 'status') === 'APPLIED').length
                : '—',
              tone: 'warning',
            },
            {
              label: '处理中',
              value: loaded
                ? items.filter((item) =>
                    ['APPROVED', 'PROCESSING'].includes(textOf(item, 'status')),
                  ).length
                : '—',
            },
            {
              label: '成功',
              value: loaded
                ? items.filter((item) => textOf(item, 'status') === 'SUCCEEDED').length
                : '—',
              tone: 'success',
            },
          ]}
        />
        <section className="card">
          <StateView state={refunds} size={items.length} noun="退款记录">
            <DataTable
              items={ledgerItems}
              columns={[
                ['orderNo', '订单号'],
                ['status', '状态'],
                ['refundAmount', '退款金额'],
                ['reason', '退款原因'],
                ['createdAt', '申请时间'],
              ]}
              {...(pageId === 'ADM-E08' && canProcessRefund
                ? {
                    actionLabel: '处理此退款',
                    onAction: (item: JsonRecord) => setSelectedRecordId(textOf(item, 'id')),
                  }
                : {})}
            />
          </StateView>
        </section>
        {pageId === 'ADM-E08' &&
          canProcessRefund &&
          selectedRefund &&
          textOf(selectedRefund, 'status') === 'APPLIED' && (
            <RefundReviewPanel refund={selectedRefund} appId={appId} onDone={refunds.reload} />
          )}
        {pageId === 'ADM-E08' &&
          canProcessRefund &&
          selectedRefund &&
          ['APPROVED', 'PROCESSING', 'FAILED'].includes(textOf(selectedRefund, 'status')) && (
            <M4Command
              title="执行或重试原路退款"
              endpoint={`/api/admin/m4/refunds/${encodeURIComponent(
                textOf(selectedRefund, 'id'),
              )}/execute?appId=${encodeURIComponent(appId)}`}
              method="POST"
              initialBody="{}"
              idempotent={false}
              onDone={refunds.reload}
            />
          )}
      </>
    );
  }
  const messageState = pageId === 'ADM-I01' ? templates : tasks;
  const items = unwrapList(messageState.data).map((item) =>
    pageId === 'ADM-I02' && isRecord(item.template)
      ? { ...item, templateCode: textOf(item.template, 'code') }
      : item,
  );
  const selectedTemplate = items.find((item) => textOf(item, 'id') === selectedRecordId);
  return (
    <>
      <PageHeader
        title={pageId === 'ADM-I01' ? '消息模板' : '消息发送记录'}
        description="消息发送是异步任务；页面只呈现服务端任务状态。"
      />
      <section className="card">
        <StateView state={messageState} size={items.length} noun="消息数据">
          <DataTable
            items={items}
            columns={
              pageId === 'ADM-I01'
                ? [
                    ['code', '模板编码'],
                    ['name', '模板名称'],
                    ['channel', '渠道'],
                    ['providerTemplateId', '微信模板 ID'],
                    ['subscriptionMode', '订阅类型'],
                    ['status', '状态'],
                  ]
                : [
                    ['templateCode', '模板'],
                    ['status', '状态'],
                    ['attemptCount', '尝试次数'],
                    ['lastError', '最后错误'],
                  ]
            }
            {...(pageId === 'ADM-I01'
              ? {
                  actionLabel: '编辑此模板',
                  onAction: (item: JsonRecord) => setSelectedRecordId(textOf(item, 'id')),
                }
              : {})}
          />
        </StateView>
      </section>
      {pageId === 'ADM-I01' && (
        <>
          <M4Command
            title="创建消息模板"
            endpoint={`/api/admin/m4/message-templates?appId=${encodeURIComponent(appId)}`}
            method="POST"
            initialBody={JSON.stringify({
              code: 'PAYMENT_CONFIRMED',
              name: '支付成功通知',
              channel: 'WECHAT_SUBSCRIBE',
              providerTemplateId: '',
              subscriptionMode: 'ONE_TIME',
              contentSchema: { allowedBindings: ['order.orderNo', 'order.status'] },
            })}
            idempotent={false}
            onDone={templates.reload}
          />
          {selectedTemplate && (
            <M4Command
              title={`编辑消息模板 · ${textOf(selectedTemplate, 'code')}`}
              endpoint={`/api/admin/m4/message-templates/${encodeURIComponent(textOf(selectedTemplate, 'id'))}?appId=${encodeURIComponent(appId)}`}
              method="PUT"
              initialBody={JSON.stringify({
                expectedVersion: Number(selectedTemplate.version ?? 0),
                name: textOf(selectedTemplate, 'name'),
                channel: textOf(selectedTemplate, 'channel'),
                providerTemplateId: textOf(selectedTemplate, 'providerTemplateId'),
                subscriptionMode: textOf(selectedTemplate, 'subscriptionMode') || 'ONE_TIME',
                contentSchema: isRecord(selectedTemplate.contentSchema)
                  ? selectedTemplate.contentSchema
                  : {},
                status: textOf(selectedTemplate, 'status'),
              })}
              idempotent={false}
              onDone={templates.reload}
            />
          )}
        </>
      )}
    </>
  );
}
function StrategyEditor({
  strategy,
  appId,
  onDone,
}: {
  strategy: JsonRecord;
  appId: string;
  onDone: () => void;
}) {
  const { api } = useRuntime();
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  async function toggle() {
    if (busy) return;
    setBusy(true);
    setMessage('');
    try {
      await api.send(
        `/api/admin/m5/rescue-strategies/${encodeURIComponent(textOf(strategy, 'id'))}?appId=${encodeURIComponent(appId)}`,
        'PATCH',
        {
          enabled: strategy.enabled !== true,
          countdownMinutes: Number(strategy.countdownMinutes),
          dailyUserLimit: Number(strategy.dailyUserLimit),
          orderLimit: Number(strategy.orderLimit),
          validFrom: strategy.validFrom,
          validTo: strategy.validTo,
          expectedVersion: Number(strategy.version),
        },
      );
      setMessage(strategy.enabled === true ? '策略已安全停用' : '策略已安全启用');
      onDone();
    } catch {
      setMessage('策略状态更新失败，请稍后重试');
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="card command-panel">
      <h2>策略控制 · {textOf(strategy, 'name')}</h2>
      <p>优惠金额与商业规则来自已配置 Coupon Rule；此处只执行带版本校验的启停命令。</p>
      <div className="button-row">
        <button className="primary" disabled={busy} onClick={() => void toggle()}>
          {busy ? '提交中…' : strategy.enabled === true ? '停用策略' : '启用策略'}
        </button>
        {message && <span className="command-result">{message}</span>}
      </div>
      <CapabilityNote write />
    </section>
  );
}

// Retained only until the Round-05 migration commit is sealed; all live H routes use the
// Canonical workbench and this legacy implementation will be removed in the next cleanup batch.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function M5AdminPage({ pageId, params }: { pageId: string; params: Params }) {
  const runtime = useRuntime();
  const appId = runtime.targetAppId;
  const members = useGet(
    appId ? `/api/admin/m5/members?appId=${encodeURIComponent(appId)}` : undefined,
  );
  const memberRows = unwrapList(members.data);
  const selectedMemberId = params.id ?? (memberRows[0] ? textOf(memberRows[0], 'id') : '');
  const events = useGet(
    appId && selectedMemberId && ['ADM-F02', 'ADM-F03', 'ADM-F06', 'ADM-F07'].includes(pageId)
      ? `/api/admin/m5/members/${encodeURIComponent(selectedMemberId)}/events?appId=${encodeURIComponent(appId)}`
      : undefined,
  );
  const funnel = useGet(
    appId && ['ADM-F05', 'ADM-F07', 'ADM-H04'].includes(pageId)
      ? `/api/admin/m5/rescue-funnel?appId=${encodeURIComponent(appId)}`
      : undefined,
  );
  const strategies = useGet(
    appId && ['ADM-H01', 'ADM-H02', 'ADM-H03'].includes(pageId)
      ? `/api/admin/m5/rescue-strategies?appId=${encodeURIComponent(appId)}`
      : undefined,
  );
  if (!appId)
    return <M4Unavailable title="会员与营销" reason="请先选择目标小程序后读取 App 隔离数据。" />;

  const memberTabs = [
    { label: '会员列表', href: pathFor('ADM-F01'), active: pageId === 'ADM-F01' },
    { label: '标签分群', href: pathFor('ADM-F04'), active: pageId === 'ADM-F04' },
    { label: '转化漏斗', href: pathFor('ADM-F05'), active: pageId === 'ADM-F05' },
    { label: '支付放弃', href: pathFor('ADM-F07'), active: pageId === 'ADM-F07' },
    { label: '隐私边界', href: pathFor('ADM-F08'), active: pageId === 'ADM-F08' },
  ];
  const marketingTabs = [
    { label: '优惠活动', href: pathFor('ADM-H01'), active: pageId === 'ADM-H01' },
    { label: '挽回策略', href: pathFor('ADM-H03'), active: pageId === 'ADM-H03' },
    { label: '挽回漏斗', href: pathFor('ADM-H04'), active: pageId === 'ADM-H04' },
  ];

  if (pageId === 'ADM-H01' || pageId === 'ADM-H02') {
    const all: JsonRecord[] = unwrapList(strategies.data).map((row) => {
      const rule = isRecord(row.couponRule) ? row.couponRule : {};
      const campaign = isRecord(rule.campaign) ? rule.campaign : {};
      return {
        ...row,
        campaignId: textOf(campaign, 'id'),
        campaignCode: textOf(campaign, 'code'),
        campaignName: textOf(campaign, 'name'),
        campaignStatus: textOf(campaign, 'status'),
        ruleId: textOf(rule, 'id'),
        ruleType: textOf(rule, 'ruleType'),
        amountCents: rule.amountCents,
        minSpendCents: rule.minSpendCents,
        validMinutes: rule.validMinutes,
        stackable: rule.stackable,
      } as JsonRecord;
    });
    const rows =
      pageId === 'ADM-H02' ? all.filter((row) => !params.id || row.campaignId === params.id) : all;
    return (
      <>
        <PageHeader
          title={pageId === 'ADM-H01' ? '优惠券活动' : '优惠券规则编辑'}
          description="活动与规则来自 PostgreSQL；已发券金额/门槛是持久化快照，不随运营配置回写。"
        />
        <SectionTabs items={marketingTabs} />
        <MetricGrid
          items={[
            { label: '活动/策略', value: all.length },
            {
              label: '启用中',
              value: all.filter((row) => row['enabled'] === true || row.campaignStatus === 'ACTIVE')
                .length,
              tone: 'success',
            },
            { label: '规则版本', value: new Set(all.map((row) => textOf(row, 'ruleId'))).size },
            { label: 'App 隔离', value: '已限定', note: '仅当前目标小程序', tone: 'success' },
          ]}
        />
        <section className="card">
          <StateView state={strategies} size={rows.length} noun="优惠券规则">
            <DataTable
              items={rows}
              columns={[
                ['campaignName', '活动'],
                ['campaignStatus', '状态'],
                ['ruleType', '规则类型'],
                ['amountCents', '面额（分）'],
                ['minSpendCents', '门槛（分）'],
                ['validMinutes', '有效分钟'],
                ['stackable', '可叠加'],
              ]}
              {...(pageId === 'ADM-H01'
                ? { link: (row) => pathFor('ADM-H02', { id: textOf(row, 'campaignId') }) }
                : {})}
            />
          </StateView>
        </section>
        {pageId === 'ADM-H02' && (
          <>
            <div className="no-stock">
              规则变更通过服务端乐观锁命令执行；已发券金额、门槛和有效期快照不会被回写。
            </div>
            {rows[0] && (
              <JsonCommand
                title="更新支付挽回优惠策略"
                endpoint={`/api/admin/m5/rescue-strategies/${encodeURIComponent(textOf(rows[0], 'id'))}?appId=${encodeURIComponent(appId)}`}
                method="PATCH"
                onDone={strategies.reload}
              />
            )}
          </>
        )}
      </>
    );
  }

  if (pageId === 'ADM-H03') {
    const rows: JsonRecord[] = unwrapList(strategies.data).map(
      (row) =>
        ({
          ...row,
          couponRuleCode: isRecord(row.couponRule) ? textOf(row.couponRule, 'id') : '—',
        }) as JsonRecord,
    );
    return (
      <>
        <PageHeader
          title="支付挽回策略"
          description="策略与优惠规则来自 PostgreSQL；商户健康不参与挽回资格计算。"
        />
        <SectionTabs items={marketingTabs} />
        <MetricGrid
          items={[
            { label: '策略总数', value: rows.length },
            {
              label: '启用策略',
              value: rows.filter((row) => row['enabled'] === true).length,
              tone: 'success',
            },
            {
              label: '每日触达上限',
              value: rows.reduce((sum, row) => sum + Number(row['dailyUserLimit'] ?? 0), 0),
            },
            { label: '商户健康耦合', value: '无', note: '资格独立计算', tone: 'success' },
          ]}
        />
        <section className="card">
          <StateView state={strategies} size={rows.length} noun="挽回策略">
            <DataTable
              items={rows}
              columns={[
                ['name', '策略'],
                ['enabled', '启用'],
                ['countdownMinutes', '有效分钟'],
                ['dailyUserLimit', '每日上限'],
                ['orderLimit', '订单上限'],
                ['version', '版本'],
              ]}
            />
          </StateView>
        </section>
        {rows.map((strategy) => (
          <StrategyEditor
            key={textOf(strategy, 'id')}
            strategy={strategy}
            appId={appId}
            onDone={strategies.reload}
          />
        ))}
      </>
    );
  }

  if (['ADM-F05', 'ADM-H04'].includes(pageId)) {
    const rows = unwrapList(funnel.data);
    return (
      <>
        <PageHeader
          title={pageId === 'ADM-H04' ? '支付挽回漏斗' : '转化漏斗'}
          description="聚合值由服务端 App 范围内的持久化事件计算。"
        />
        <SectionTabs items={pageId === 'ADM-H04' ? marketingTabs : memberTabs} />
        <MetricGrid
          items={[
            { label: '漏斗阶段', value: rows.length },
            {
              label: '累计事件',
              value: rows.reduce((sum, row) => sum + Number(row.count ?? 0), 0),
            },
            {
              label: '关联金额（分）',
              value: rows.reduce((sum, row) => sum + Number(row.amountCents ?? 0), 0),
            },
            { label: '数据范围', value: '当前 App', tone: 'success' },
          ]}
        />
        <section className="card">
          <h2>阶段分布</h2>
          <StateView state={funnel} size={rows.length} noun="漏斗事件">
            <BarSummary
              items={rows.map((row) => ({
                label: textOf(row, 'eventType') || '未命名阶段',
                value: Number(row.count ?? 0),
                note: `${Number(row.amountCents ?? 0)} 分`,
              }))}
            />
            <div className="section-heading">
              <h2>聚合明细</h2>
              <span>服务端 App 范围</span>
            </div>
            <DataTable
              items={rows}
              columns={[
                ['eventType', '阶段'],
                ['count', '人数/次数'],
                ['amountCents', '金额（分）'],
              ]}
            />
          </StateView>
        </section>
      </>
    );
  }

  if (pageId === 'ADM-F08') {
    return (
      <>
        <PageHeader
          title="会员导出隐私"
          description="普通会员 API 已服务端裁剪 OpenID、手机号密文、手机号哈希和会话密钥。"
        />
        <SectionTabs items={memberTabs} />
        <MetricGrid
          items={[
            { label: '普通接口可见', value: 'L0' },
            { label: '联系方式', value: '默认脱敏', tone: 'success' },
            { label: '密钥/会话', value: '永不返回', tone: 'success' },
            { label: '批量导出', value: '专用权限控制', note: '无权限即无入口', tone: 'success' },
          ]}
        />
        <section className="card privacy-panel">
          <h2>隐私与导出边界</h2>
          <p>
            会员批量导出由正式导出中心承载；没有专用 export 权限与审计链路时，服务端不提供导出入口。
          </p>
          <CapabilityNote write={false} />
        </section>
      </>
    );
  }

  if (pageId === 'ADM-F03' || pageId === 'ADM-F06' || pageId === 'ADM-F07') {
    const rows = unwrapList(events.data);
    const eventDistribution = Array.from(
      rows.reduce((counts, row) => {
        const name = textOf(row, 'eventName') || 'unknown';
        counts.set(name, (counts.get(name) ?? 0) + 1);
        return counts;
      }, new Map<string, number>()),
    ).map(([label, value]) => ({ label, value }));
    const title =
      pageId === 'ADM-F03'
        ? '会员行为时间线'
        : pageId === 'ADM-F06'
          ? '会员偏好分析'
          : '支付放弃人群';
    return (
      <>
        <PageHeader
          title={title}
          description="明细来自白名单行为事件；敏感身份字段禁止写入事件属性。"
        />
        <SectionTabs items={memberTabs} />
        <MetricGrid
          items={[
            { label: '行为事件', value: rows.length },
            {
              label: '涉及城市',
              value: new Set(rows.map((row) => textOf(row, 'cityId')).filter(Boolean)).size,
            },
            {
              label: '涉及产品',
              value: new Set(rows.map((row) => textOf(row, 'productId')).filter(Boolean)).size,
            },
            { label: '敏感属性', value: '禁止写入', tone: 'success' },
          ]}
        />
        <section className="card">
          <StateView state={events} size={rows.length} noun="会员行为">
            <BarSummary items={eventDistribution} />
            <div className="section-heading">
              <h2>行为明细</h2>
              <span>白名单事件</span>
            </div>
            <DataTable
              items={rows}
              columns={[
                ['eventTime', '时间'],
                ['eventName', '事件'],
                ['pageId', 'Page-ID'],
                ['cityId', '城市'],
                ['productId', '产品'],
                ['skuId', 'SKU'],
              ]}
            />
          </StateView>
        </section>
      </>
    );
  }

  if (pageId === 'ADM-F02') {
    const member = memberRows.find((row) => textOf(row, 'id') === selectedMemberId) ?? {};
    return (
      <>
        <PageHeader
          title="会员 360 详情"
          description="身份引用已裁剪；仅展示当前 App Scope 的会员聚合摘要。"
        />
        <SectionTabs
          items={[
            { label: '会员概览', href: pathFor('ADM-F02', { id: selectedMemberId }), active: true },
            { label: '行为时间线', href: pathFor('ADM-F03', { id: selectedMemberId }) },
          ]}
        />
        <MetricGrid
          items={[
            { label: '会员状态', value: textOf(member, 'status') || '—' },
            { label: '标签数', value: Array.isArray(member.tags) ? member.tags.length : 0 },
            {
              label: '关联身份',
              value: Array.isArray(member.linkedApps) ? member.linkedApps.length : 0,
            },
            { label: '隐私字段', value: '已裁剪', tone: 'success' },
          ]}
        />
        <section className="card">
          <StateView state={members} size={Object.keys(member).length} noun="会员">
            <DetailGrid
              record={member}
              fields={[
                ['id', '会员 ID'],
                ['status', '状态'],
                ['firstCityId', '首次城市'],
                ['firstSeenAt', '首次访问'],
                ['lastSeenAt', '最近访问'],
                ['linkedApps', '当前 App 身份'],
                ['tags', '标签'],
              ]}
            />
          </StateView>
        </section>
      </>
    );
  }

  const rows =
    pageId === 'ADM-F04'
      ? memberRows.map((member) => ({
          ...member,
          tagsDisplay: Array.isArray(member.tags)
            ? member.tags
                .map((tag) => (isRecord(tag) ? textOf(tag, 'name') : ''))
                .filter(Boolean)
                .join('、')
            : '—',
        }))
      : memberRows;
  return (
    <>
      <PageHeader
        title={pageId === 'ADM-F04' ? '会员标签分群' : '会员列表'}
        description="列表来自服务端 App Scope，默认不返回 L1/L3 隐私字段。"
      />
      <SectionTabs items={memberTabs} />
      <MetricGrid
        items={[
          { label: '会员总数', value: rows.length },
          {
            label: '启用会员',
            value: rows.filter((row) => textOf(row, 'status') === 'ACTIVE').length,
            tone: 'success',
          },
          {
            label: '已标记会员',
            value: rows.filter((row) => Array.isArray(row.tags) && row.tags.length > 0).length,
          },
          { label: '数据范围', value: '当前 App', tone: 'success' },
        ]}
      />
      <section className="card">
        <StateView state={members} size={rows.length} noun="会员">
          <DataTable
            items={rows}
            columns={[
              ['id', '会员 ID'],
              ['status', '状态'],
              ['firstCityId', '首次城市'],
              ['lastSeenAt', '最近访问'],
              ...(pageId === 'ADM-F04' ? [['tagsDisplay', '标签'] as [string, string]] : []),
            ]}
            link={(member) => pathFor('ADM-F02', { id: textOf(member, 'id') })}
          />
        </StateView>
      </section>
    </>
  );
}

function AlertCenterPage({
  api,
  appId,
  state,
}: {
  api: AdminApiClient;
  appId: string;
  state: LoadState<unknown>;
}) {
  const snapshot = unwrapRecord(state.data);
  const durable = unwrapRecord(snapshot.durable);
  const alerts = listField(durable, 'alerts');
  const rules = listField(durable, 'alertRules');
  const openAlerts = Number(durable.openAlerts ?? alerts.length);
  const [selectedCode, setSelectedCode] = useState('');
  const selected = rules.find((rule) => textOf(rule, 'code') === selectedCode) ?? rules[0];
  const [severity, setSeverity] = useState('P1');
  const [metricKey, setMetricKey] = useState('api.availability');
  const [comparator, setComparator] = useState('LT');
  const [threshold, setThreshold] = useState('99.9');
  const [windowValue, setWindowValue] = useState('5m');
  const [enabled, setEnabled] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState('');
  useEffect(() => {
    if (!selected) return;
    setSelectedCode(textOf(selected, 'code'));
    setSeverity(textOf(selected, 'severity'));
    setMetricKey(textOf(selected, 'metricKey'));
    setComparator(textOf(selected, 'comparator'));
    setThreshold(String(selected.threshold ?? ''));
    setWindowValue(textOf(selected, 'window'));
    setEnabled(selected.enabled !== false);
  }, [selected]);
  async function command(path: string, method: 'POST' | 'PUT', body?: unknown) {
    setBusy(true);
    setResult('');
    try {
      await api.send(path, method, body);
      setResult('操作已保存并写入审计日志');
      state.reload();
    } catch (error) {
      setResult(error instanceof Error ? error.message : '操作失败，请稍后重试');
    } finally {
      setBusy(false);
    }
  }
  const [statusFilter, setStatusFilter] = useState('');
  const [severityFilter, setSeverityFilter] = useState('');
  const visibleAlerts = alerts.filter(
    (alert) =>
      (!statusFilter || textOf(alert, 'status') === statusFilter) &&
      (!severityFilter || textOf(nestedAdmin(alert, 'rule'), 'severity') === severityFilter),
  );
  return (
    <div className="erp-base-workspace">
      <PageHeader title="后台消息告警中心" description="处理当前告警并维护通知规则" />
      <StateView
        state={state}
        size={Object.keys(durable).length + alerts.length + rules.length}
        noun="消息告警数据"
      >
        <div className="erp-base-statline">
          <span>
            未关闭告警 <b>{openAlerts}</b>
          </span>
          <span>
            可靠任务待处理 <b>{Number(durable.outboxPending ?? 0)}</b>
          </span>
          <span>
            可靠任务失败 <b>{Number(durable.outboxFailed ?? 0)}</b>
          </span>
          <span>
            消息积压 <b>{Number(durable.messageBacklog ?? 0)}</b>
          </span>
        </div>
        <div className="erp-base-split">
          <section className="card">
            <h2>当前告警与处理队列</h2>
            <div className="erp-base-toolbar">
              <label>
                告警状态
                <select
                  value={statusFilter}
                  onChange={(event) => setStatusFilter(event.target.value)}
                >
                  <option value="">全部状态</option>
                  <option value="OPEN">待处理</option>
                  <option value="ACKNOWLEDGED">已知悉</option>
                  <option value="RESOLVED">已解决</option>
                </select>
              </label>
              <label>
                告警等级
                <select
                  value={severityFilter}
                  onChange={(event) => setSeverityFilter(event.target.value)}
                >
                  <option value="">全部等级</option>
                  <option value="P0">P0 紧急</option>
                  <option value="P1">P1 重要</option>
                  <option value="P2">P2 提醒</option>
                </select>
              </label>
              <button type="button" disabled={busy} onClick={state.reload}>
                刷新
              </button>
            </div>
            <div className="canonical-table-wrap">
              <table className="erp-base-table">
                <thead>
                  <tr>
                    <th>等级</th>
                    <th>告警指标</th>
                    <th>观测值 / 阈值</th>
                    <th>发生时间</th>
                    <th>状态</th>
                    <th>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleAlerts.map((alert) => {
                    const rule = nestedAdmin(alert, 'rule');
                    const eventId = textOf(alert, 'id');
                    const status = textOf(alert, 'status');
                    return (
                      <tr key={eventId}>
                        <td>
                          <span
                            className={`admin-severity ${textOf(rule, 'severity').toLowerCase()}`}
                          >
                            {displayText(rule.severity, 'P1')}
                          </span>
                        </td>
                        <td>{metricText(rule.metricKey)}</td>
                        <td>
                          {displayText(alert.observedValue, '—')} /{' '}
                          {comparatorText(rule.comparator)} {displayText(rule.threshold, '—')}
                        </td>
                        <td>{adminDate(alert.openedAt)}</td>
                        <td>{alertStatusText(status)}</td>
                        <td>
                          <div className="button-row">
                            {status === 'OPEN' && (
                              <button
                                type="button"
                                disabled={busy}
                                onClick={() =>
                                  void command(
                                    `/api/admin/m8/apps/${encodeURIComponent(appId)}/ops/alerts/${encodeURIComponent(eventId)}/acknowledge`,
                                    'POST',
                                  )
                                }
                              >
                                标记已知悉
                              </button>
                            )}
                            {status !== 'RESOLVED' && (
                              <button
                                type="button"
                                disabled={busy}
                                onClick={() =>
                                  void command(
                                    `/api/admin/m8/apps/${encodeURIComponent(appId)}/ops/alerts/${encodeURIComponent(eventId)}/resolve`,
                                    'POST',
                                  )
                                }
                              >
                                关闭告警
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {!visibleAlerts.length && (
                <p className="state-card compact">
                  {alerts.length ? '没有符合筛选条件的告警。' : '当前没有未关闭告警'}
                </p>
              )}
            </div>
            <p className="erp-base-facts-note">
              告警确认与关闭保留审计记录。支付、退款和发布操作请从相应业务入口办理。
            </p>
          </section>
          <section className="card">
            <h2>规则维护</h2>
            {rules.length ? (
              <div className="erp-base-rule-form">
                <label>
                  规则
                  <select
                    value={selectedCode}
                    onChange={(event) => setSelectedCode(event.target.value)}
                  >
                    {rules.map((rule) => (
                      <option key={textOf(rule, 'id')} value={textOf(rule, 'code')}>
                        {metricText(rule.metricKey)}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  等级
                  <select value={severity} onChange={(event) => setSeverity(event.target.value)}>
                    <option value="P0">P0 紧急</option>
                    <option value="P1">P1 重要</option>
                    <option value="P2">P2 提醒</option>
                  </select>
                </label>
                <label>
                  指标
                  <select value={metricKey} onChange={(event) => setMetricKey(event.target.value)}>
                    {Object.entries(metricLabels).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  比较方式
                  <select
                    value={comparator}
                    onChange={(event) => setComparator(event.target.value)}
                  >
                    {Object.entries(comparatorLabels).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  阈值
                  <input
                    inputMode="decimal"
                    value={threshold}
                    onChange={(event) => setThreshold(event.target.value)}
                  />
                </label>
                <label>
                  时间窗口
                  <input
                    value={windowValue}
                    onChange={(event) => setWindowValue(event.target.value)}
                  />
                </label>
                <label className="admin-checkbox">
                  <input
                    type="checkbox"
                    checked={enabled}
                    onChange={(event) => setEnabled(event.target.checked)}
                  />
                  启用该规则
                </label>
                <button
                  className="primary"
                  disabled={
                    busy ||
                    !selectedCode ||
                    !metricKey ||
                    !threshold.trim() ||
                    !windowValue.trim() ||
                    !Number.isFinite(Number(threshold))
                  }
                  onClick={() =>
                    void command(
                      `/api/admin/m8/apps/${encodeURIComponent(appId)}/ops/alert-rules/${encodeURIComponent(selectedCode)}`,
                      'PUT',
                      {
                        severity,
                        metricKey,
                        comparator,
                        threshold: Number(threshold),
                        window: windowValue,
                        enabled,
                      },
                    )
                  }
                >
                  {busy ? '保存中…' : '保存规则'}
                </button>
              </div>
            ) : (
              <p className="state-card compact">暂无可维护规则。</p>
            )}
          </section>
        </div>
        {result && <p role="status">{result}</p>}
      </StateView>
    </div>
  );
}

export function LegacyAccessScopePage({ state }: { state: LoadState<unknown> }) {
  const snapshot = unwrapRecord(state.data);
  const roles = listField(snapshot, 'roles');
  const scopes = listField(snapshot, 'dataScopes');
  const fieldPolicies = listField(snapshot, 'fieldPolicies');
  const statePolicies = listField(snapshot, 'statePolicies');
  const [roleId, setRoleId] = useState('');
  const selectedRole = roles.find((role) => textOf(role, 'id') === roleId) ?? roles[0];
  const permissions = selectedRole ? permissionRefs(selectedRole) : [];
  const selectedRoleId = textOf(selectedRole ?? {}, 'id');
  const roleScopes = scopes.filter(
    (scope) =>
      textOf(scope, 'roleId') === selectedRoleId ||
      textOf(nestedAdmin(scope, 'role'), 'id') === selectedRoleId,
  );
  const roleFieldPolicies = fieldPolicies.filter(
    (policy) =>
      textOf(policy, 'roleId') === selectedRoleId ||
      textOf(nestedAdmin(policy, 'role'), 'id') === selectedRoleId,
  );
  const scopeGroups = Array.from(
    countByLabel(roleScopes, (scope) => scopeTypeText(scope.scopeType)),
  );
  const fieldGroups = Array.from(
    countByLabel(
      roleFieldPolicies,
      (policy) =>
        `${displayText(policy.classification, '字段等级')} · ${fieldAccessText(policy.access)}`,
    ),
  );
  return (
    <div className="erp-base-workspace">
      <PageHeader title="权限与数据范围" description="查看授权角色、可用功能和数据范围" />
      <StateView
        state={state}
        size={roles.length + scopes.length + fieldPolicies.length}
        noun="权限数据"
      >
        <div className="erp-base-toolbar">
          <label>
            查看角色
            <select
              value={selectedRole ? textOf(selectedRole, 'id') : ''}
              onChange={(event) => setRoleId(event.target.value)}
            >
              {roles.map((role) => (
                <option key={textOf(role, 'id')} value={textOf(role, 'id')}>
                  {displayText(role.name, '运营角色')}
                </option>
              ))}
            </select>
          </label>
          <Link href={pathFor('ADM-J02')}>维护角色</Link>
          <Link href={pathFor('ADM-J04')}>配置数据范围</Link>
          <Link href={pathFor('ADM-J05')}>配置字段权限</Link>
        </div>
        <div className="erp-base-split">
          <section className="card">
            <h2>角色与功能权限</h2>
            <div className="canonical-table-wrap">
              <table className="erp-base-table">
                <thead>
                  <tr>
                    <th>功能</th>
                    <th>授权类型</th>
                    <th>操作限制</th>
                  </tr>
                </thead>
                <tbody>
                  {permissions.map((ref, index) => {
                    const permission = nestedAdmin(ref, 'permission');
                    return (
                      <tr key={`${textOf(permission, 'id')}-${index}`}>
                        <td>{permissionName(permission)}</td>
                        <td>{permission.highRisk === true ? '高风险权限' : '普通权限'}</td>
                        <td>
                          {permission.highRisk === true
                            ? '需满足独立授权与业务状态'
                            : '按数据范围执行'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {!permissions.length && (
                <p className="state-card compact">该角色暂无可见功能权限。</p>
              )}
            </div>
            <p className="erp-base-facts-note">未授权功能默认拒绝；实际操作由服务端再次校验。</p>
          </section>
          <section className="card">
            <h2>数据与字段边界</h2>
            <div className="canonical-table-wrap">
              <table className="erp-base-table">
                <thead>
                  <tr>
                    <th>范围类型</th>
                    <th>授权项数</th>
                  </tr>
                </thead>
                <tbody>
                  {scopeGroups.map(([name, count]) => (
                    <tr key={name}>
                      <td>{name}</td>
                      <td>{count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="canonical-table-wrap">
              <table className="erp-base-table">
                <thead>
                  <tr>
                    <th>字段等级 / 访问策略</th>
                    <th>策略数</th>
                  </tr>
                </thead>
                <tbody>
                  {fieldGroups.map(([name, count]) => (
                    <tr key={name}>
                      <td>{name}</td>
                      <td>{count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="erp-base-facts-note">
              以上为所选角色的策略。系统另有 {statePolicies.length}{' '}
              项业务状态规则；联系方式默认脱敏，财务字段需单独授权，密钥证书不通过普通业务接口返回。
            </p>
          </section>
        </div>
      </StateView>
    </div>
  );
}

function AuditOverviewPage({ state }: { state: LoadState<unknown> }) {
  const snapshot = unwrapRecord(state.data);
  const auditLogs = listField(snapshot, 'auditLogs');
  const [keyword, setKeyword] = useState('');
  const [riskOnly, setRiskOnly] = useState(false);
  const [page, setPage] = useState(1);
  const highRiskLogs = auditLogs.filter((log) => isHighRiskAudit(log.action));
  const tracedLogs = auditLogs.filter((log) => log.requestId || log.traceId);
  const needle = keyword.trim().toLocaleLowerCase();
  const visible = auditLogs.filter(
    (log) =>
      (!riskOnly || isHighRiskAudit(log.action)) &&
      (!needle ||
        `${auditActionText(log.action)} ${auditObjectText(log.objectType)} ${auditActorText(log.actorSnapshot)}`
          .toLocaleLowerCase()
          .includes(needle)),
  );
  const pageSize = 14;
  const pageCount = Math.max(1, Math.ceil(visible.length / pageSize));
  const currentPage = Math.min(page, pageCount);
  return (
    <div className="erp-base-workspace">
      <PageHeader title="审计概览" description="查询当前授权范围内的操作记录" />
      <StateView state={state} size={auditLogs.length} noun="审计记录">
        <div className="erp-base-statline">
          <span>
            审计记录 <b>{auditLogs.length}</b>
          </span>
          <span>
            高风险动作 <b>{highRiskLogs.length}</b>
          </span>
          <span>
            可追踪请求 <b>{tracedLogs.length}</b>
          </span>
        </div>
        <section className="erp-base-toolbar">
          <label>
            关键词
            <input
              type="search"
              value={keyword}
              placeholder="操作 / 对象 / 操作者"
              onChange={(event) => {
                setKeyword(event.target.value);
                setPage(1);
              }}
            />
          </label>
          <label>
            <input
              type="checkbox"
              checked={riskOnly}
              onChange={(event) => {
                setRiskOnly(event.target.checked);
                setPage(1);
              }}
            />
            只看高风险操作
          </label>
          <button
            type="button"
            onClick={() => {
              setKeyword('');
              setRiskOnly(false);
              setPage(1);
            }}
          >
            重置
          </button>
          <button type="button" onClick={state.reload}>
            刷新记录
          </button>
        </section>
        <section className="card">
          <h2>最近审计轨迹</h2>
          <div className="canonical-table-wrap">
            <table className="erp-base-table">
              <thead>
                <tr>
                  <th>发生时间</th>
                  <th>操作</th>
                  <th>操作对象</th>
                  <th>操作者</th>
                  <th>风险等级</th>
                  <th>请求链路</th>
                </tr>
              </thead>
              <tbody>
                {visible
                  .slice((currentPage - 1) * pageSize, currentPage * pageSize)
                  .map((log, index) => (
                    <tr key={displayText(log.id, String(index))}>
                      <td>{adminDate(log.occurredAt)}</td>
                      <td>{auditActionText(log.action)}</td>
                      <td>{auditObjectText(log.objectType)}</td>
                      <td>{auditActorText(log.actorSnapshot)}</td>
                      <td>{isHighRiskAudit(log.action) ? '高风险' : '常规'}</td>
                      <td>{log.requestId || log.traceId ? '已记录' : '未提供'}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
            {!visible.length && <p className="state-card compact">没有符合筛选条件的审计记录。</p>}
          </div>
          <div className="erp-base-page-footer">
            <span>
              共 {visible.length} 条 · 每页 {pageSize} 条
            </span>
            <button
              type="button"
              disabled={currentPage <= 1}
              onClick={() => setPage(currentPage - 1)}
            >
              上一页
            </button>
            <span>
              {currentPage} / {pageCount}
            </span>
            <button
              type="button"
              disabled={currentPage >= pageCount}
              onClick={() => setPage(currentPage + 1)}
            >
              下一页
            </button>
          </div>
        </section>
      </StateView>
    </div>
  );
}

function M9AdminPage({ pageId }: { pageId: string }) {
  const runtime = useRuntime();
  const appId = runtime.targetAppId;
  const [loginAccount, setLoginAccount] = useState(runtime.production ? '' : 'dev-m2-admin');
  const [loginPassword, setLoginPassword] = useState('');
  const [captcha, setCaptcha] = useState<AdminCaptchaChallenge | null>(null);
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [captchaRevision, setCaptchaRevision] = useState(0);
  const [captchaPending, setCaptchaPending] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [loginPending, setLoginPending] = useState(false);
  const [keyword, setKeyword] = useState('');
  const encoded = appId ? encodeURIComponent(appId) : '';
  const overview = useGet(
    appId && pageId === 'ADM-A05' ? `/api/admin/m4/orders?appId=${encoded}` : undefined,
  );
  const ops = useGet(
    appId && pageId === 'ADM-A06' ? `/api/admin/m8/apps/${encoded}/ops/snapshot` : undefined,
  );
  const security = useGet(
    appId && pageId === 'ADM-A08' ? `/api/admin/m8/apps/${encoded}/security` : undefined,
  );
  useEffect(() => {
    if (pageId !== 'ADM-A01' || !runtime.appId) return;
    let active = true;
    setCaptchaPending(true);
    runtime.api
      .getAdminCaptcha(runtime.appId)
      .then((result) => {
        if (!active) return;
        setCaptcha(result);
        setCaptchaAnswer('');
      })
      .catch((error) => {
        if (!active) return;
        setCaptcha(null);
        setLoginError(error instanceof Error ? error.message : '验证码加载失败，请刷新页面重试');
      })
      .finally(() => {
        if (active) setCaptchaPending(false);
      });
    return () => {
      active = false;
    };
  }, [captchaRevision, pageId, runtime.api, runtime.appId]);
  if (pageId === 'ADM-A01') {
    return (
      <main className="admin-login">
        <section className="admin-login__story" aria-label="平台能力概览">
          <div className="admin-login__brand">
            <span className="admin-login__brand-mark">旅</span>
            <div>
              <small>统一运营中台</small>
              <strong>城市一日游综合旅游服务平台</strong>
            </div>
          </div>
          <h1>
            多城市 · 多小程序 · 多 SKU
            <br />
            统一运营，权限可控
          </h1>
          <p>
            面向包车、接送、拼车等综合旅游服务产品。
            <br />
            后台集中维护城市、产品、订单、会员、支付、渠道与装修发布。
          </p>
          <div className="admin-login__capabilities">
            <article>
              <strong>严格权限体系</strong>
              <span>功能 × 数据范围 × 字段 × 业务状态</span>
            </article>
            <article>
              <strong>多小程序矩阵</strong>
              <span>独立前端配置，中央订单池统一处理</span>
            </article>
            <article>
              <strong>安全登录审计</strong>
              <span>设备、IP、登录日志与验证码防刷</span>
            </article>
            <article>
              <strong>高并发运营基线</strong>
              <span>交易与行为链路隔离，可观测可恢复</span>
            </article>
          </div>
        </section>
        <section className="admin-login__stage">
          <div className="admin-login__panel">
            <header>
              <div>
                <h2>管理后台登录</h2>
                <p>安全验证</p>
              </div>
              <span>{runtime.production ? '生产环境' : '开发 / 测试'}</span>
            </header>
            <nav className="admin-login__tabs" aria-label="登录方式">
              <span className="active">账号登录</span>
            </nav>
            <div className="admin-login__form">
              <label>
                <span>登录账号</span>
                <input
                  autoComplete="username"
                  placeholder="请输入员工账号"
                  value={loginAccount}
                  disabled={loginPending}
                  onChange={(event) => setLoginAccount(event.target.value)}
                />
              </label>
              <label>
                <span>登录密码</span>
                <input
                  autoComplete="current-password"
                  type="password"
                  placeholder="请输入登录密码"
                  value={loginPassword}
                  disabled={loginPending}
                  onChange={(event) => setLoginPassword(event.target.value)}
                />
              </label>
              <div className="admin-login__captcha">
                <label>
                  <span>验证码</span>
                  <input
                    autoComplete="one-time-code"
                    inputMode="numeric"
                    maxLength={3}
                    placeholder="请输入右侧计算结果"
                    value={captchaAnswer}
                    disabled={loginPending || captchaPending || !captcha}
                    onChange={(event) =>
                      setCaptchaAnswer(event.target.value.replace(/\D/gu, '').slice(0, 3))
                    }
                  />
                </label>
                <button
                  type="button"
                  disabled={loginPending || captchaPending || !runtime.appId}
                  onClick={() => {
                    setLoginError('');
                    setCaptchaRevision((value) => value + 1);
                  }}
                  aria-label="刷新验证码"
                >
                  {captchaPending ? '加载中' : (captcha?.question ?? '刷新验证码')}
                </button>
              </div>
              <div className="admin-login__remember">
                <span title="首版不保存长期登录">关闭页面后需重新登录</span>
                <span>忘记密码或账号异常，请联系系统管理员</span>
              </div>
              <ActionButton
                className="primary admin-login__submit"
                pending={loginPending}
                pendingLabel="登录中…"
                disabled={
                  loginPending ||
                  captchaPending ||
                  !runtime.appId ||
                  !loginAccount ||
                  !loginPassword ||
                  !captcha ||
                  !captchaAnswer
                }
                onClick={async () => {
                  setLoginPending(true);
                  setLoginError('');
                  try {
                    if (!captcha) throw new Error('验证码未加载，请刷新后重试');
                    const result = await runtime.api.loginAdmin(
                      runtime.appId,
                      loginAccount,
                      loginPassword,
                      captcha.challengeId,
                      captchaAnswer,
                    );
                    runtime.setCredentials(runtime.appId, result.accessToken, {
                      user: result.user,
                      expiresAt: result.expiresAt,
                    });
                    history.pushState({}, '', '/admin/dashboard');
                    window.dispatchEvent(new PopStateEvent('popstate'));
                  } catch (error) {
                    setLoginError(error instanceof Error ? error.message : '登录失败，请稍后重试');
                    setCaptchaRevision((value) => value + 1);
                  } finally {
                    setLoginPending(false);
                  }
                }}
              >
                登录
              </ActionButton>
              <Notice tone={loginError ? 'error' : 'info'}>
                {loginError ||
                  (runtime.appId
                    ? '使用员工账号、密码和验证码登录。登录不会创建员工或自动授予权限。'
                    : '后台应用标识未配置，请检查 VITE_ADMIN_APP_ID。')}
              </Notice>
            </div>
          </div>
          <footer>© 2026 城市一日游综合旅游服务平台 · 仅限授权人员使用 · 全程安全审计</footer>
        </section>
      </main>
    );
  }
  if (pageId === 'ADM-A02') {
    return <CanonicalDashboardPage api={runtime.api} targetAppId={runtime.targetAppId} />;
  }
  if (!appId)
    return (
      <M4Unavailable
        title="经营工作台"
        reason="请先选择目标小程序；所有经营数据按 App Scope 读取。"
      />
    );
  if (pageId === 'ADM-A06') {
    return <AlertCenterPage api={runtime.api} appId={appId} state={ops} />;
  }
  if (pageId === 'ADM-A05') {
    return <AdminGlobalSearchPage state={overview} keyword={keyword} setKeyword={setKeyword} />;
  }
  if (pageId === 'ADM-A07') return <BusinessSecurityMyAccessPage api={runtime.api} />;
  if (pageId === 'ADM-A08') return <AuditOverviewPage state={security} />;
  return (
    <M4Unavailable
      title={adminPages.find((page) => page.id === pageId)?.title ?? '经营工作台'}
      reason="该页面已登记但当前路由未匹配到业务工作台。"
    />
  );
}

function RoutedPage({ pageId, params }: { pageId: string; params: Params }) {
  const runtime = useRuntime();
  if (pageId.startsWith('ADM-L'))
    return (
      <AdLandingHost
        api={runtime.api}
        pageId={pageId}
        appId={runtime.pageScope.appId}
        onAppChange={(appId) => runtime.setPageScope({ appId, cityId: '', page: 1 })}
        {...(params.id ? { landingId: params.id } : {})}
      />
    );
  if (pageId === 'ADM-A09')
    return (
      <AccountCenterPage
        api={runtime.api}
        onPasswordChanged={runtime.clearSession}
        onProfileChanged={(profile) => runtime.updateDisplayName(profile.displayName)}
      />
    );
  if (pageId === 'ADM-A03')
    return <CanonicalLiveTransactionsPage api={runtime.api} targetAppId={runtime.targetAppId} />;
  if (pageId === 'ADM-A04')
    return <CanonicalTodoCenterPage api={runtime.api} targetAppId={runtime.targetAppId} />;
  if (pageId.startsWith('ADM-A')) return <M9AdminPage pageId={pageId} />;
  if (pageId.startsWith('ADM-J'))
    return (
      <CanonicalSecurityReleasePage
        api={runtime.api}
        targetAppId={runtime.targetAppId}
        pageId={pageId}
        params={params}
      />
    );
  if (pageId.startsWith('ADM-I'))
    return (
      <CanonicalMessageFinancePage
        api={runtime.api}
        targetAppId={runtime.targetAppId}
        pageId={pageId}
        params={params}
      />
    );
  if (pageId.startsWith('ADM-F'))
    return (
      <CanonicalMemberPage
        api={runtime.api}
        targetAppId={runtime.targetAppId}
        pageId={pageId}
        params={params}
      />
    );
  if (pageId.startsWith('ADM-H'))
    return (
      <CanonicalMarketingChannelPage
        api={runtime.api}
        targetAppId={runtime.targetAppId}
        pageId={pageId}
        params={params}
      />
    );
  if (pageId === 'ADM-B01') return <CityPages pageId={pageId} params={params} />;
  if (pageId === 'ADM-B02' || pageId === 'ADM-B03')
    return (
      <CanonicalCityDetailPage
        api={runtime.api}
        targetAppId={runtime.targetAppId}
        pageId={pageId}
        cityId={params.id ?? ''}
      />
    );
  if (pageId.startsWith('ADM-B'))
    return (
      <CanonicalAppPage
        api={runtime.api}
        targetAppId={runtime.targetAppId}
        pageId={pageId}
        {...(params.id ? { appId: params.id } : {})}
      />
    );
  if (pageId === 'ADM-C01') return <ProductListPage pageId={pageId} />;
  if (pageId === 'ADM-C08')
    return (
      <CanonicalTagsSortPage
        api={runtime.api}
        targetAppId={runtime.targetAppId === AUTHORIZED_SCOPE ? '' : runtime.targetAppId}
      />
    );
  if (pageId === 'ADM-C05' || pageId === 'ADM-C06')
    return (
      <CanonicalSkuPage
        api={runtime.api}
        pageId={pageId}
        skuId={params.id ?? ''}
        targetAppId={runtime.targetAppId}
        scopeFilter={
          <PageScopeFilter
            api={runtime.api}
            dataset="products"
            mode="operation"
            scope={runtime.pageScope}
            onChange={runtime.setPageScope}
            showPagination={false}
          />
        }
      />
    );
  if (pageId.startsWith('ADM-C'))
    return (
      <CanonicalProductPage
        api={runtime.api}
        targetAppId={runtime.targetAppId}
        pageId={
          ['ADM-C03', 'ADM-C04', 'ADM-C07', 'ADM-C09', 'ADM-C10'].includes(pageId)
            ? 'ADM-C02'
            : pageId
        }
        productId={params.id ?? ''}
        scopeFilter={
          <PageScopeFilter
            api={runtime.api}
            dataset="products"
            mode="operation"
            compact={['ADM-C02', 'ADM-C03', 'ADM-C04', 'ADM-C07', 'ADM-C09', 'ADM-C10'].includes(
              pageId,
            )}
            scope={runtime.pageScope}
            onChange={runtime.setPageScope}
            showPagination={false}
          />
        }
      />
    );
  if (pageId.startsWith('ADM-D'))
    return (
      <CanonicalCmsPage
        api={runtime.api}
        targetAppId={runtime.targetAppId}
        pageId={pageId}
        params={params}
      />
    );
  if (pageId.startsWith('ADM-E'))
    return (
      <CanonicalOrderPage
        api={runtime.api}
        targetAppId={runtime.targetAppId}
        pageId={pageId}
        params={params}
        {...(['ADM-E01', 'ADM-E03', 'ADM-E04', 'ADM-E05'].includes(pageId)
          ? {
              scopeFilter: (
                <PageScopeFilter
                  api={runtime.api}
                  dataset="orders"
                  scope={runtime.pageScope}
                  onChange={runtime.setPageScope}
                  showPagination={false}
                />
              ),
              scopePagination: (
                <PageScopePagination
                  dataset="orders"
                  scope={runtime.pageScope}
                  onChange={runtime.setPageScope}
                />
              ),
              onFiltersChange: () => runtime.setPageScope({ ...runtime.pageScope, page: 1 }),
            }
          : {})}
      />
    );
  if (['ADM-G09'].includes(pageId) || pageId.startsWith('ADM-I'))
    return <M4AdminPage pageId={pageId} params={params} />;
  if (pageId.startsWith('ADM-G'))
    return (
      <CanonicalPaymentPage
        api={runtime.api}
        targetAppId={runtime.targetAppId}
        pageId={pageId}
        params={params}
      />
    );
  return <NotFound />;
}
function readSession(key: string, fallback = '') {
  try {
    return sessionStorage.getItem(key) ?? fallback;
  } catch {
    return fallback;
  }
}
function clearStoredAdminSession() {
  try {
    sessionStorage.removeItem('travel.admin.token');
    sessionStorage.removeItem('travel.admin.user');
    sessionStorage.removeItem('travel.admin.expiresAt');
  } catch {
    // A storage-denied browser still receives the in-memory logout below.
  }
}
function readSessionUser(): AdminLoginSession['user'] | null {
  try {
    const raw = sessionStorage.getItem('travel.admin.user');
    if (!raw) return null;
    const value: unknown = JSON.parse(raw);
    if (!isRecord(value) || typeof value.id !== 'string' || typeof value.displayName !== 'string') {
      return null;
    }
    return { id: value.id, displayName: value.displayName };
  } catch {
    return null;
  }
}
export function resolveBootAuth(input: {
  production: boolean;
  envAppId: string;
  envToken: string;
  sessionAppId: string;
  sessionToken: string;
}) {
  const appId = input.sessionAppId || input.envAppId;
  if (input.production && input.envToken) {
    return {
      appId,
      token: input.sessionToken,
      configError: 'production 禁止采用 VITE_ADMIN_TOKEN；必须使用服务端签发的正式会话。',
    };
  }
  return { appId, token: input.sessionToken || input.envToken };
}

export function resolveAdminRuntimeEnvironment(input: {
  viteProduction: boolean;
  appEnvironment?: string;
}) {
  const environment = input.appEnvironment?.trim().toLowerCase();
  return environment ? environment === 'production' : input.viteProduction;
}

export function resolveInitialAdminPath(pathname: string, token: string) {
  const requestedPath = pathname === '/' ? '/admin/dashboard' : pathname;
  return token || requestedPath === '/admin/login' ? requestedPath : '/admin/login';
}

export function App() {
  const production = resolveAdminRuntimeEnvironment({
    viteProduction: import.meta.env.PROD,
    appEnvironment: import.meta.env.VITE_APP_ENV,
  });
  const bootAuth = resolveBootAuth({
    production,
    envAppId: import.meta.env.VITE_ADMIN_APP_ID ?? '',
    envToken: import.meta.env.VITE_ADMIN_TOKEN ?? '',
    sessionAppId: readSession('travel.admin.appId'),
    sessionToken: readSession('travel.admin.token'),
  });
  const storedExpiresAt = readSession('travel.admin.expiresAt');
  const bootSessionExpired = Boolean(
    storedExpiresAt && new Date(storedExpiresAt).getTime() <= Date.now(),
  );
  if (bootSessionExpired) clearStoredAdminSession();
  const initialToken = bootSessionExpired ? '' : bootAuth.token;
  const initialPath = resolveInitialAdminPath(location.pathname, initialToken);
  if (location.pathname !== initialPath) history.replaceState({}, '', initialPath);
  const [locationKey, setLocationKey] = useState(`${initialPath}${location.search}`);
  const pathname = locationKey.split('?')[0] ?? initialPath;
  const [appId, setAppId] = useState(bootAuth.appId);
  const [token, setToken] = useState(initialToken);
  const [user, setUser] = useState<AdminLoginSession['user'] | null>(() =>
    bootSessionExpired ? null : readSessionUser(),
  );
  const [expiresAt, setExpiresAt] = useState(bootSessionExpired ? '' : storedExpiresAt);
  const productConfiguration = [
    'ADM-C02',
    'ADM-C03',
    'ADM-C04',
    'ADM-C05',
    'ADM-C06',
    'ADM-C07',
    'ADM-C09',
    'ADM-C10',
  ].includes(matchPage(pathname)?.page.id ?? '');
  const appScopedConfiguration =
    productConfiguration || (matchPage(pathname)?.page.id ?? '').startsWith('ADM-L');
  const scopeRoute = appScopedConfiguration ? locationKey : pathname;
  const detailAppHint =
    appScopedConfiguration || matchPage(pathname)?.page.id === 'ADM-G06'
      ? (new URLSearchParams(locationKey.split('?')[1] ?? '').get('appId') ?? '')
      : '';
  const [scopeState, setScopeState] = useState<{ route: string; value: PageScope }>({
    route: scopeRoute,
    value: { appId: detailAppHint, cityId: '' },
  });
  const pageScope: PageScope = {
    ...(scopeState.route === scopeRoute ? scopeState.value : { appId: detailAppHint, cityId: '' }),
    dataset: PAGE_DATASETS[matchPage(pathname)?.page.id ?? ''],
  };
  const scopeRef = useRef(pageScope);
  scopeRef.current = pageScope;
  const explicitApp = /^\/admin\/apps\/([^/]+)\//u.exec(pathname)?.[1];
  const targetAppId = explicitApp || pageScope.appId || AUTHORIZED_SCOPE;
  const clearSession = () => {
    setToken('');
    setUser(null);
    setExpiresAt('');
    clearStoredAdminSession();
    history.replaceState({}, '', '/admin/login');
    setLocationKey('/admin/login');
  };
  const api = useMemo(
    () =>
      new AuthorizedScopeApiClient(
        () => ({ appId, token }),
        import.meta.env.VITE_API_BASE_URL ?? '',
        () => clearSession(),
        () => scopeRef.current,
      ),
    [appId, token],
  );
  useEffect(() => {
    const listener = () => {
      const target = `${location.pathname}${location.search}`;
      const commit = () => {
        if (`${location.pathname}${location.search}` !== target) history.pushState({}, '', target);
        setLocationKey(target);
      };
      const navigating = new CustomEvent('travel:admin-navigation', {
        cancelable: true,
        detail: { commit, from: locationKey, to: target },
      });
      if (window.dispatchEvent(navigating)) commit();
      else history.replaceState({}, '', locationKey);
    };
    addEventListener('popstate', listener);
    return () => removeEventListener('popstate', listener);
  }, [locationKey]);
  useEffect(() => {
    if (!token || !expiresAt) return;
    const remaining = new Date(expiresAt).getTime() - Date.now();
    if (remaining <= 0) {
      clearSession();
      return;
    }
    const timeout = window.setTimeout(clearSession, Math.min(remaining, 2_147_000_000));
    return () => window.clearTimeout(timeout);
  }, [token, expiresAt]);
  const runtime = useMemo<AdminRuntime>(
    () => ({
      api,
      appId,
      token,
      targetAppId,
      production,
      user,
      expiresAt,
      pageScope,
      setPageScope: (value) => {
        if (!appScopedConfiguration) {
          setScopeState({ route: pathname, value });
          return;
        }
        const query = new URLSearchParams(locationKey.split('?')[1] ?? '');
        if (value.appId) query.set('appId', value.appId);
        else query.delete('appId');
        const target = `${pathname}${query.size ? `?${query}` : ''}`;
        const commit = () => {
          setScopeState({ route: target, value });
          history.replaceState({}, '', target);
          setLocationKey(target);
        };
        if (target === locationKey) commit();
        else if (
          window.dispatchEvent(
            new CustomEvent('travel:admin-navigation', {
              cancelable: true,
              detail: { commit, from: locationKey, to: target },
            }),
          )
        )
          commit();
      },
      clearSession,
      updateDisplayName: (value) => {
        setUser((current) => {
          if (!current) return current;
          const next = { ...current, displayName: value };
          sessionStorage.setItem('travel.admin.user', JSON.stringify(next));
          return next;
        });
      },
      ...(bootAuth.configError ? { configError: bootAuth.configError } : {}),
      setCredentials: (nextAppId, nextToken, session) => {
        setAppId(nextAppId);
        setToken(nextToken);
        setUser(session?.user ?? null);
        setExpiresAt(session?.expiresAt ?? '');
        sessionStorage.setItem('travel.admin.appId', nextAppId);
        sessionStorage.setItem('travel.admin.token', nextToken);
        if (session) {
          sessionStorage.setItem('travel.admin.user', JSON.stringify(session.user));
          sessionStorage.setItem('travel.admin.expiresAt', session.expiresAt);
        } else {
          sessionStorage.removeItem('travel.admin.user');
          sessionStorage.removeItem('travel.admin.expiresAt');
        }
      },
      logout: async () => {
        try {
          await api.logout();
        } finally {
          clearSession();
        }
      },
      setTargetAppId: (value) => {
        setScopeState({
          route: pathname,
          value: { appId: value === AUTHORIZED_SCOPE ? '' : value, cityId: '' },
        });
      },
    }),
    [api, appId, token, targetAppId, production, user, expiresAt, pageScope, locationKey],
  );
  const matched = matchPage(pathname);
  const page = matched?.page ?? { id: 'UNREGISTERED', title: '未登记页面' };
  return (
    <RuntimeContext.Provider value={runtime}>
      {page.id === 'ADM-A01' && matched ? (
        <RoutedPage pageId={page.id} params={matched.params} />
      ) : (
        <AdminShell pageId={page.id} title={page.title}>
          {matched ? (
            <RoutedPage
              key={`${page.id}:${pageScope.appId}:${pageScope.cityId}:${pageScope.page ?? 1}`}
              pageId={page.id}
              params={matched.params}
            />
          ) : (
            <NotFound />
          )}
        </AdminShell>
      )}
    </RuntimeContext.Provider>
  );
}
